import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Home, ArrowLeft, Plus, DollarSign, Users, Mail, Trash2, 
  Send, Copy, Check, RefreshCw, X, Calendar, User, CreditCard, ChevronDown, ChevronUp,
  Camera, AlertTriangle, Lock, LayoutGrid, List, Table2, CheckCircle2, FileText, Edit2, Search
} from 'lucide-react';
import MobileNav from '../components/MobileNav';

const API = process.env.REACT_APP_BACKEND_URL;

export default function MomsHousePage() {
  const navigate = useNavigate();
  
  // Contacts State
  const [contacts, setContacts] = useState([]);
  const [loadingContacts, setLoadingContacts] = useState(true);
  const [showAddContact, setShowAddContact] = useState(false);
  const [newContact, setNewContact] = useState({ name: '', email: '' });
  const [savingContact, setSavingContact] = useState(false);
  
  // Renovation Expenses State
  const [expenses, setExpenses] = useState([]);
  // Search query for filtering renovation expenses by description / notes /
  // vendor names / card name.  Case-insensitive, matches anywhere in the text.
  const [expenseSearch, setExpenseSearch] = useState('');
  const [loadingExpenses, setLoadingExpenses] = useState(true);
  const [showAddExpense, setShowAddExpense] = useState(false);
  const [newExpense, setNewExpense] = useState({
    description: '', amount: '', charge_amount: '', date: new Date().toISOString().split('T')[0], notes: '', card_used: ''
  });
  const [newExpensePaidInFull, setNewExpensePaidInFull] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [savingExpense, setSavingExpense] = useState(false);
  
  // Personal Tabs State
  const [personalTabs, setPersonalTabs] = useState([]);
  const [loadingTabs, setLoadingTabs] = useState(true);
  const [personalExpenses, setPersonalExpenses] = useState([]);
  const [loadingPersonalExpenses, setLoadingPersonalExpenses] = useState(false);
  const [personalTotal, setPersonalTotal] = useState(0);
  const [showAddPersonalExpense, setShowAddPersonalExpense] = useState(false);
  const [newPersonalExpense, setNewPersonalExpense] = useState({
    description: '', amount: '', date: new Date().toISOString().split('T')[0], category: '', notes: '', paid_by: '', card_used: ''
  });
  const [savingPersonalExpense, setSavingPersonalExpense] = useState(false);
  const [editingTabEmail, setEditingTabEmail] = useState(null);
  const [tabEmailInput, setTabEmailInput] = useState('');
  const [sendingTabEmail, setSendingTabEmail] = useState(null);
  
  // Editing personal expense state
  const [editingExpense, setEditingExpense] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [savingEdit, setSavingEdit] = useState(false);
  
  // Personal Tab Payments (received from this person)
  const [personalPayments, setPersonalPayments] = useState([]);
  const [loadingPersonalPayments, setLoadingPersonalPayments] = useState(false);
  const [showAddPersonalPayment, setShowAddPersonalPayment] = useState(false);
  const [newPersonalPayment, setNewPersonalPayment] = useState({
    amount: '', date: new Date().toISOString().split('T')[0], method: 'Zelle', notes: '', send_receipt: true,
  });
  const [savingPersonalPayment, setSavingPersonalPayment] = useState(false);
  
  // UI State
  const [activeTab, setActiveTab] = useState('renovations');
  // Contacts tab was removed 2026-02-08 — auto-redirect users whose saved
  // tab state still points to it so they don't hit an invisible dead-end.
  useEffect(() => {
    if (activeTab === 'contacts') setActiveTab('renovations');
  }, [activeTab]);
  const [copiedLink, setCopiedLink] = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(null);
  
  // Credit Card State
  const [cards, setCards] = useState([]);
  const [editingCard, setEditingCard] = useState(null);
  const [cardBalanceInput, setCardBalanceInput] = useState('');

  // ── Credit card spend summary (Feb 26, 2026) ──
  // Per-card real spend + Gregory / Randall invoiced totals. Rendered INSIDE
  // the My View bracket on the renovations tab (per user request: "inside of
  // the bracket when it says my view").
  const cardsSpendSummary = React.useMemo(() => {
    const realSpendByCardName = {};
    expenses.forEach(exp => {
      const realAmt = parseFloat(exp.amount) || 0;
      if (realAmt <= 0) return;
      const payments = exp.payments || [];
      if (payments.length > 0) {
        payments.forEach(p => {
          const key = (p.card_used || exp.card_used || '').trim().toLowerCase();
          if (!key) return;
          realSpendByCardName[key] = (realSpendByCardName[key] || 0) + (parseFloat(p.amount) || 0);
        });
      } else {
        const key = (exp.card_used || '').trim().toLowerCase();
        if (key) realSpendByCardName[key] = (realSpendByCardName[key] || 0) + realAmt;
      }
    });
    // Family invoiced totals — bucketed by contact first name so "Greg" and
    // "Gregory" both map to gregoryTotal.
    const contactIdToName = {};
    contacts.forEach(c => { contactIdToName[c.id] = (c.name || '').trim(); });
    const totalsByContact = {};
    expenses.forEach(exp => {
      const ca = exp.charge_amount;
      const charge = (ca === null || ca === undefined || ca === '') ? (parseFloat(exp.amount) || 0) : (parseFloat(ca) || 0);
      if (charge <= 0) return;
      const notify = exp.notify_contacts || [];
      notify.forEach(cid => {
        const bucket = (contactIdToName[cid] || '').toLowerCase().split(/\s+/)[0] || cid;
        totalsByContact[bucket] = (totalsByContact[bucket] || 0) + charge;
      });
    });
    return {
      realSpendByCardName,
      gregoryTotal: totalsByContact.greg || totalsByContact.gregory || 0,
      randallTotal: totalsByContact.randal || totalsByContact.randall || 0,
    };
  }, [expenses, contacts]);

  /**
   * Search-filtered renovation expenses.  Matches against description,
   * notes, vendor/card name, notify-contact names, and any payment notes
   * — so typing "garage" surfaces every transaction with that word
   * anywhere in the record.  Empty query returns everything.
   *
   * NOTE: `expense.card_used` and `payment.card_used` may hold the card
   * ID (linked card) OR a free-text card name (older records).  We look
   * up the card by ID and include its name in the searchable haystack
   * so typing "Red Card" or "Wells Fargo" surfaces every transaction on
   * that card.
   */
  const filteredExpenses = React.useMemo(() => {
    const q = (expenseSearch || '').trim().toLowerCase();
    if (!q) return expenses;
    const contactNameById = {};
    contacts.forEach(c => { contactNameById[c.id] = (c.name || '').toLowerCase(); });
    const cardNameById = {};
    cards.forEach(c => { cardNameById[c.id] = (c.name || '').toLowerCase(); });
    const resolveCard = (idOrName) => {
      if (!idOrName) return '';
      // If it looks like an ID (matches a known card id), use its name.
      const asName = cardNameById[idOrName];
      return asName || String(idOrName).toLowerCase();
    };
    return expenses.filter(exp => {
      const paymentBits = (exp.payments || [])
        .map(p => `${p.notes || ''} ${resolveCard(p.card_used)}`)
        .join(' ');
      const haystack = [
        exp.description, exp.notes,
        resolveCard(exp.card_used),
        (exp.notify_contacts || []).map(cid => contactNameById[cid] || '').join(' '),
        paymentBits,
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(q);
    });
  }, [expenses, expenseSearch, contacts, cards]);

  // Edit-everywhere state (Apr 29, 2026)
  const [editingRenovPayment, setEditingRenovPayment] = useState(null); // { expenseId, paymentId }
  const [renovPayForm, setRenovPayForm] = useState({ amount: '', date: '', card_used: '', notes: '' });
  const [editingPersonalPayment, setEditingPersonalPayment] = useState(null); // payment id
  const [personalPayForm, setPersonalPayForm] = useState({ amount: '', date: '', method: '', notes: '' });
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({ name: '', email: '' });

  // ── Final Credit Card Balance tab (Feb 26, 2026) ──
  // Aggregates EVERY charge made on each card across renovations, personal
  // tabs (Randal/Greg/etc), and contractor invoices. Renovation home page
  // stays renovation-only — this tab is where you see the full picture.
  const [cardBalancesData, setCardBalancesData] = useState({}); // { cardId: { items, total } }
  const [loadingCardBalances, setLoadingCardBalances] = useState(false);

  const fetchAllCardBalances = useCallback(async () => {
    if (!cards || cards.length === 0) return;
    try {
      setLoadingCardBalances(true);
      const out = {};
      await Promise.all(cards.map(async (c) => {
        try {
          const r = await axios.get(`${API}/api/moms-house/cards/${c.id}/breakdown?scope=all`);
          out[c.id] = r.data;
        } catch (e) {
          out[c.id] = { items: [], total_spent: 0 };
        }
      }));
      setCardBalancesData(out);
    } finally {
      setLoadingCardBalances(false);
    }
  }, [cards]);

  // Auto-load when the user opens the Card Balances tab
  useEffect(() => {
    if (activeTab === 'cardbalances') {
      fetchAllCardBalances();
    }
  }, [activeTab, fetchAllCardBalances]);

  // ── Credit Card Payback tab (2026-02-08 user request) ──
  // Payback total per card MUST equal what the user sees on the small
  // renovation-only tiles PLUS the Greg / Randal personal-tab charges
  // for that card.  We derive that directly from the cards list
  // endpoint (already loaded in `cards`) — which returns
  // `breakdown.renovations` (real cost) and `breakdown.personal_total`
  // (all personal-tab charges on that card).  This is guaranteed to
  // match the numbers on the House Renovations tab and the personal
  // tabs because they all read from the same source.
  const PAYBACK_CARD_IDS = ['red', 'gray', 'white', 'wells'];
  const [paybackExpanded, setPaybackExpanded] = useState({});
  // Per-card ITEMIZED list (renovation payments + personal tab items).
  // Fetched lazily when the card is expanded (keeps the tab snappy
  // when only totals matter).
  const [paybackItems, setPaybackItems] = useState({}); // {cardId: [items]}
  const [paybackItemsLoading, setPaybackItemsLoading] = useState({});

  const paybackTotals = React.useMemo(() => {
    const out = {};
    (cards || []).forEach((c) => {
      const b = c.breakdown || {};
      // renovations = real-cost sum on renovation section
      // personal_total = sum of ALL personal tab charges on this card
      // (already includes Greg + Randal + everyone else — matches the
      // small tiles on each personal tab).
      const renov = Number(b.renovations || 0);
      const personal = Number(b.personal_total || 0);
      out[c.id] = {
        card: c,
        renovations: renov,
        personal: personal,
        total: renov + personal,
      };
    });
    return out;
  }, [cards]);

  const paybackGrandTotal = React.useMemo(() => {
    return PAYBACK_CARD_IDS.reduce(
      (acc, cid) => acc + Number(paybackTotals[cid]?.total || 0),
      0,
    );
  }, [paybackTotals]);

  // Lazily load the itemized list for a card when the user expands it.
  const loadPaybackItems = useCallback(async (cid) => {
    if (paybackItems[cid]) return; // cached
    setPaybackItemsLoading((s) => ({ ...s, [cid]: true }));
    try {
      // scope=all captures renovation payments + personal expenses on
      // this card — but we filter OUT contractor rows to match the
      // renovation-real-cost + personal-tab spec.
      const r = await axios.get(`${API}/api/moms-house/cards/${cid}/breakdown?scope=all`);
      const items = (r.data?.items || []).filter(
        (it) => it.type === 'renovation_payment' || it.type === 'personal',
      );
      setPaybackItems((prev) => ({ ...prev, [cid]: items }));
    } catch (e) {
      setPaybackItems((prev) => ({ ...prev, [cid]: [] }));
    } finally {
      setPaybackItemsLoading((s) => ({ ...s, [cid]: false }));
    }
  }, [paybackItems]);

  // ── Credit card payment-reminder cards (Feb 26, 2026) ──
  // Per user request: 3 cards used for renovations with monthly due day.
  // Reminders fire 1 and 2 days before each due date via APScheduler.
  const [creditReminderCards, setCreditReminderCards] = useState([]);
  const [editingReminderCard, setEditingReminderCard] = useState(null);
  const [reminderCardForm, setReminderCardForm] = useState({ name: '', due_day: '', limit_label: '' });
  const [showReminderCardManager, setShowReminderCardManager] = useState(false);

  const fetchCreditReminderCards = async () => {
    try {
      const r = await axios.get(`${API}/api/moms-house/credit-cards`);
      setCreditReminderCards(r.data.cards || []);
    } catch (err) {
      console.error('Failed to load credit-card reminders', err);
    }
  };

  // Handlers for the reminder-cards manager
  const startEditReminderCard = (c) => {
    setEditingReminderCard(c.id);
    setReminderCardForm({ name: c.name || '', due_day: c.due_day || '', limit_label: c.limit_label || '' });
  };
  const startAddReminderCard = () => {
    setEditingReminderCard('new');
    setReminderCardForm({ name: '', due_day: '', limit_label: '' });
  };
  const saveReminderCard = async () => {
    const name = (reminderCardForm.name || '').trim();
    const dueDay = parseInt(reminderCardForm.due_day, 10);
    if (!name) { toast.error('Card name required'); return; }
    if (!Number.isFinite(dueDay) || dueDay < 1 || dueDay > 31) { toast.error('Due day must be 1-31'); return; }
    try {
      if (editingReminderCard === 'new') {
        await axios.post(`${API}/api/moms-house/credit-cards`, {
          name, due_day: dueDay, limit_label: (reminderCardForm.limit_label || '').trim(),
        });
        toast.success(`${name} added — reminders will fire 1 & 2 days before day ${dueDay}`);
      } else {
        await axios.put(`${API}/api/moms-house/credit-cards/${editingReminderCard}`, {
          name, due_day: dueDay, limit_label: (reminderCardForm.limit_label || '').trim(),
        });
        toast.success(`${name} updated`);
      }
      setEditingReminderCard(null);
      fetchCreditReminderCards();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    }
  };
  const deleteReminderCard = async (cardId, name) => {
    if (!window.confirm(`Stop reminders for ${name}?`)) return;
    try {
      await axios.delete(`${API}/api/moms-house/credit-cards/${cardId}`);
      toast.success('Reminder card removed');
      fetchCreditReminderCards();
    } catch (err) {
      toast.error('Delete failed');
    }
  };
  const sendTestReminder = async (cardId, name) => {
    try {
      const r = await axios.post(`${API}/api/moms-house/credit-cards/${cardId}/test-reminder?days=1`);
      toast.success(r.data.sent ? `Test reminder sent for ${name} — check your inbox` : 'Send failed — check GMAIL_EMAIL config');
    } catch (err) {
      toast.error('Test send failed');
    }
  };

  // Compute next due date for a card (returns a Date or null)
  const nextDueDate = (dueDay) => {
    const dd = parseInt(dueDay, 10);
    if (!Number.isFinite(dd) || dd < 1 || dd > 31) return null;
    const today = new Date();
    const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
    let target = Math.min(dd, lastDay);
    let candidate = new Date(today.getFullYear(), today.getMonth(), target);
    if (candidate < new Date(today.getFullYear(), today.getMonth(), today.getDate())) {
      const nm = new Date(today.getFullYear(), today.getMonth() + 1, 1);
      const nmLast = new Date(nm.getFullYear(), nm.getMonth() + 1, 0).getDate();
      target = Math.min(dd, nmLast);
      candidate = new Date(nm.getFullYear(), nm.getMonth(), target);
    }
    return candidate;
  };
  
  // Fetch data on mount
  useEffect(() => {
    fetchContacts();
    fetchExpenses();
    fetchPersonalTabs();
    fetchCards();
    fetchCreditReminderCards();
  }, []);
  
  const fetchContacts = async () => {
    try {
      setLoadingContacts(true);
      const response = await axios.get(`${API}/api/moms-house/contacts`);
      setContacts(response.data.contacts || []);
    } catch (error) {
      console.error('Failed to fetch contacts:', error);
    } finally {
      setLoadingContacts(false);
    }
  };
  
  const fetchExpenses = async () => {
    try {
      setLoadingExpenses(true);
      const response = await axios.get(`${API}/api/moms-house/expenses`);
      setExpenses(response.data.expenses || []);
    } catch (error) {
      console.error('Failed to fetch expenses:', error);
    } finally {
      setLoadingExpenses(false);
    }
  };
  
  const fetchPersonalTabs = async () => {
    try {
      setLoadingTabs(true);
      const response = await axios.get(`${API}/api/moms-house/personal-tabs`);
      setPersonalTabs(response.data.tabs || []);
    } catch (error) {
      console.error('Failed to fetch personal tabs:', error);
    } finally {
      setLoadingTabs(false);
    }
  };
  
  const fetchCards = async () => {
    try {
      const response = await axios.get(`${API}/api/moms-house/cards`);
      setCards(response.data.cards || []);
    } catch (error) {
      console.error('Failed to fetch cards:', error);
    }
  };
  
  const handleUpdateCardBalance = async (cardId) => {
    try {
      const bal = parseFloat(cardBalanceInput);
      if (isNaN(bal)) { toast.error('Enter a valid number'); return; }
      await axios.put(`${API}/api/moms-house/cards/${cardId}`, { balance: bal });
      setCards(cards.map(c => c.id === cardId ? { ...c, balance: bal } : c));
      setEditingCard(null);
      toast.success('Card balance updated');
    } catch (error) {
      toast.error('Failed to update');
    }
  };
  
  const fetchPersonalExpenses = useCallback(async (tabId) => {
    try {
      setLoadingPersonalExpenses(true);
      const response = await axios.get(`${API}/api/moms-house/personal-tabs/${tabId}/expenses`);
      setPersonalExpenses(response.data.expenses || []);
      setPersonalTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to fetch personal expenses:', error);
    } finally {
      setLoadingPersonalExpenses(false);
    }
  }, []);
  
  const fetchPersonalPayments = useCallback(async (tabId) => {
    try {
      setLoadingPersonalPayments(true);
      const response = await axios.get(`${API}/api/moms-house/personal-tabs/${tabId}/payments`);
      setPersonalPayments(response.data.payments || []);
    } catch (error) {
      console.error('Failed to fetch personal payments:', error);
    } finally {
      setLoadingPersonalPayments(false);
    }
  }, []);
  
  // Load personal expenses + payments when switching to a personal tab
  useEffect(() => {
    if (activeTab !== 'renovations' && activeTab !== 'contacts') {
      const tab = personalTabs.find(t => t.id === activeTab);
      if (tab) {
        fetchPersonalExpenses(tab.id);
        fetchPersonalPayments(tab.id);
      }
    }
  }, [activeTab, personalTabs, fetchPersonalExpenses, fetchPersonalPayments]);
  
  const handleAddPersonalPayment = async () => {
    const amt = parseFloat(newPersonalPayment.amount);
    if (isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      setSavingPersonalPayment(true);
      const resp = await axios.post(`${API}/api/moms-house/personal-tabs/${activeTab}/payments`, {
        ...newPersonalPayment, amount: amt,
      });
      if (resp.data.success) {
        setPersonalPayments([resp.data.payment, ...personalPayments]);
        toast.success(`Recorded ${formatCurrency(amt)} payment`);
        if (resp.data.receipt_sent) toast.success('Receipt emailed');
        else if (newPersonalPayment.send_receipt) toast.message('Set their email to send a receipt');
        setNewPersonalPayment({ amount: '', date: new Date().toISOString().split('T')[0], method: 'Zelle', notes: '', send_receipt: true });
        setShowAddPersonalPayment(false);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record payment');
    } finally {
      setSavingPersonalPayment(false);
    }
  };
  
  const handleDeletePersonalPayment = async (paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await axios.delete(`${API}/api/moms-house/personal-tabs/${activeTab}/payments/${paymentId}`);
      setPersonalPayments(personalPayments.filter(p => p.id !== paymentId));
      toast.success('Payment removed');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const startEditPersonalPayment = (p) => {
    setEditingPersonalPayment(p.id);
    setPersonalPayForm({
      amount: p.amount ?? '',
      date: p.date || '',
      method: p.method || 'Zelle',
      notes: p.notes || '',
    });
  };
  const handleSavePersonalPayment = async () => {
    const amt = parseFloat(personalPayForm.amount);
    if (isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      const resp = await axios.put(
        `${API}/api/moms-house/personal-tabs/${activeTab}/payments/${editingPersonalPayment}`,
        { ...personalPayForm, amount: amt }
      );
      if (resp.data.success) {
        setPersonalPayments(personalPayments.map(p => p.id === editingPersonalPayment ? { ...p, ...personalPayForm, amount: amt } : p));
        setEditingPersonalPayment(null);
        toast.success('Payment updated');
      }
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  const startEditRenovPayment = (expenseId, p) => {
    setEditingRenovPayment({ expenseId, paymentId: p.id });
    setRenovPayForm({
      amount: p.amount ?? '',
      date: p.date || '',
      card_used: p.card_used || '',
      notes: p.notes || '',
    });
  };
  const handleSaveRenovPayment = async () => {
    if (!editingRenovPayment) return;
    const amt = parseFloat(renovPayForm.amount);
    if (isNaN(amt) || amt <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      const resp = await axios.put(
        `${API}/api/moms-house/expenses/${editingRenovPayment.expenseId}/payments/${editingRenovPayment.paymentId}`,
        { ...renovPayForm, amount: amt }
      );
      if (resp.data.success) {
        await fetchExpenses();
        await fetchCards();
        setEditingRenovPayment(null);
        toast.success('Payment updated');
      }
    } catch (e) {
      toast.error('Failed to update');
    }
  };
  const handleDeleteRenovPayment = async (expenseId, paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      await axios.delete(`${API}/api/moms-house/expenses/${expenseId}/payments/${paymentId}`);
      await fetchExpenses();
      await fetchCards();
      toast.success('Payment removed');
    } catch (e) {
      toast.error('Failed to delete');
    }
  };

  const startEditContact = (c) => {
    setEditingContact(c.id);
    setContactForm({ name: c.name || '', email: c.email || '' });
  };
  const handleSaveContact = async () => {
    if (!contactForm.name.trim() || !contactForm.email.trim()) { toast.error('Name and email required'); return; }
    try {
      const resp = await axios.put(`${API}/api/moms-house/contacts/${editingContact}`, contactForm);
      if (resp.data.success) {
        setContacts(contacts.map(c => c.id === editingContact ? resp.data.contact : c));
        setEditingContact(null);
        toast.success('Contact updated');
      }
    } catch (e) {
      toast.error('Failed to update');
    }
  };

  // Add Contact
  const handleAddContact = async () => {
    if (!newContact.name.trim() || !newContact.email.trim()) {
      toast.error('Please enter name and email');
      return;
    }
    try {
      setSavingContact(true);
      const response = await axios.post(`${API}/api/moms-house/contacts`, newContact);
      if (response.data.success) {
        toast.success(`Added ${newContact.name}`);
        setContacts([...contacts, response.data.contact]);
        setNewContact({ name: '', email: '' });
        setShowAddContact(false);
      }
    } catch (error) {
      toast.error('Failed to add contact');
    } finally {
      setSavingContact(false);
    }
  };
  
  const handleDeleteContact = async (contactId, contactName) => {
    if (!window.confirm(`Remove ${contactName}?`)) return;
    try {
      await axios.delete(`${API}/api/moms-house/contacts/${contactId}`);
      setContacts(contacts.filter(c => c.id !== contactId));
      toast.success(`Removed ${contactName}`);
    } catch (error) {
      toast.error('Failed to remove contact');
    }
  };
  
  // Renovation Expenses
  const handleAddExpense = async () => {
    if (!newExpense.description.trim() || !newExpense.amount) {
      toast.error('Please enter description and amount');
      return;
    }
    const amount = parseFloat(newExpense.amount);
    if (isNaN(amount) || amount <= 0) { toast.error('Invalid amount'); return; }
    // Family charge defaults to real cost if not provided
    const chargeAmount = newExpense.charge_amount ? parseFloat(newExpense.charge_amount) : amount;
    if (isNaN(chargeAmount) || chargeAmount < 0) { toast.error('Invalid family charge amount'); return; }
    
    try {
      setSavingExpense(true);
      const response = await axios.post(`${API}/api/moms-house/expenses`, {
        ...newExpense, amount, charge_amount: chargeAmount, notify_contacts: selectedContacts, card_used: newExpense.card_used
      });
      if (response.data.success) {
        let createdExpense = response.data.expense;
        // Per user request 2026-02-26: every entered expense is automatically
        // considered paid in full (no separate "Paid" button needed). Send
        // paid_amount = amount immediately after creating the expense.
        try {
          const putRes = await axios.put(`${API}/api/moms-house/expenses/${createdExpense.id}`, { paid_amount: amount });
          if (putRes.data.success) createdExpense = { ...createdExpense, ...putRes.data.expense };
        } catch (err) {
          console.error('Auto paid-in-full update failed', err);
        }
        toast.success(`Added: ${newExpense.description}`);
        setExpenses([createdExpense, ...expenses]);
        setNewExpense({ description: '', amount: '', charge_amount: '', date: new Date().toISOString().split('T')[0], notes: '', card_used: '' });
        setNewExpensePaidInFull(false);
        setSelectedContacts([]);
        setShowAddExpense(false);
        if (response.data.emails_sent > 0) toast.success(`Notified ${response.data.emails_sent} family member(s)`);
        fetchCards();
      }
    } catch (error) {
      toast.error('Failed to add expense');
    } finally {
      setSavingExpense(false);
    }
  };
  
  const handleDeleteExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/api/moms-house/expenses/${expenseId}`);
      setExpenses(expenses.filter(e => e.id !== expenseId));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };
  
  // Payment tracking state
  const [expandedPayment, setExpandedPayment] = useState(null);
  const [showAddPayment, setShowAddPayment] = useState(null);
  const [newPayment, setNewPayment] = useState({ amount: '', date: new Date().toISOString().split('T')[0], notes: '', card_used: '' });
  const [savingPayment, setSavingPayment] = useState(false);
  const [paymentEmailContacts, setPaymentEmailContacts] = useState([]);
  
  // Editing renovation expense state
  const [editingRenovation, setEditingRenovation] = useState(null);
  const [renovationEditForm, setRenovationEditForm] = useState({});
  const [savingRenovation, setSavingRenovation] = useState(false);
  
  // Per-expense "Paid So Far" inline editor
  const [paidInputs, setPaidInputs] = useState({}); // { [expenseId]: stringValue }
  const [savingPaidId, setSavingPaidId] = useState(null);
  
  // Renovations view mode: 'cards' | 'compact' | 'table'
  const [renovView, setRenovView] = useState(() => localStorage.getItem('momsHouseRenovView') || 'cards');
  useEffect(() => { localStorage.setItem('momsHouseRenovView', renovView); }, [renovView]);
  
  // Renovations sort (for table view)
  const [renovSort, setRenovSort] = useState({ key: 'date', dir: 'desc' });
  
  // Duplicate suspicion: same description (case-insensitive) + same amount; flag any with date within 7 days
  const duplicateGroups = React.useMemo(() => {
    const groups = {};
    (expenses || []).forEach(e => {
      const key = `${(e.description || '').trim().toLowerCase()}|${Number(e.amount || 0).toFixed(2)}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(e);
    });
    return Object.values(groups).filter(g => g.length > 1);
  }, [expenses]);
  const duplicateExpenseIds = React.useMemo(() => {
    const s = new Set();
    duplicateGroups.forEach(g => g.forEach(e => s.add(e.id)));
    return s;
  }, [duplicateGroups]);
  const [showDuplicates, setShowDuplicates] = useState(false);

  // "Mark Paid in Full" — creates a payment LOG entry for the remaining balance,
  // so we have a date-stamped record that the bill was settled (vs. just bumping paid_amount).
  const [markingPaidId, setMarkingPaidId] = useState(null);
  const handleMarkPaidInFull = async (expense) => {
    const paid = getPaidAmount(expense);
    const remaining = (expense.amount || 0) - paid;
    if (remaining <= 0.001) {
      toast.info('Already fully paid.');
      return;
    }
    try {
      setMarkingPaidId(expense.id);
      const resp = await axios.post(`${API}/api/moms-house/expenses/${expense.id}/payments`, {
        amount: remaining,
        date: new Date().toISOString().split('T')[0],
        notes: 'Marked paid in full',
        card_used: expense.card_used || '',
      });
      if (resp.data?.success) {
        setExpenses(expenses.map(e => {
          if (e.id !== expense.id) return e;
          const payments = [...(e.payments || []), resp.data.payment];
          return { ...e, payments, total_paid: resp.data.total_paid, remaining_balance: resp.data.remaining_balance };
        }));
        toast.success(`✓ ${expense.description} marked paid in full (${formatCurrency(remaining)} logged)`);
        fetchCards();
      } else {
        throw new Error(resp.data?.error || 'Failed');
      }
    } catch (err) {
      toast.error('Could not mark paid in full');
    } finally {
      setMarkingPaidId(null);
    }
  };
  
  // Statement screenshot scanner
  const statementFileInputRef = useRef(null);
  const [scanningStatement, setScanningStatement] = useState(false);
  const [scannedTransactions, setScannedTransactions] = useState([]); // each: {date, merchant, amount, is_pending, is_duplicate, duplicate_of, included, edited}
  const [showScanReview, setShowScanReview] = useState(false);
  const [showScanConfirm, setShowScanConfirm] = useState(false);
  const [scanCardId, setScanCardId] = useState('');
  const [savingScannedBatch, setSavingScannedBatch] = useState(false);
  
  const handleStatementUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    e.target.value = '';
    const tooBig = files.find((f) => f.size > 10 * 1024 * 1024);
    if (tooBig) { toast.error(`"${tooBig.name}" is over 10MB`); return; }
    try {
      setScanningStatement(true);
      toast.message(`Scanning ${files.length} screenshot${files.length !== 1 ? 's' : ''}…`);
      const results = await Promise.allSettled(files.map(async (file) => {
        const fd = new FormData();
        fd.append('file', file);
        const r = await axios.post(`${API}/api/moms-house/scan-statement`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        return r.data?.transactions || [];
      }));
      const merged = [];
      let failed = 0;
      results.forEach((res, idx) => {
        if (res.status === 'fulfilled') merged.push(...res.value);
        else { failed++; console.error('OCR failed for file', idx, res.reason); }
      });
      if (merged.length === 0) {
        toast.error(failed > 0 ? `Couldn't read ${failed} screenshot${failed !== 1 ? 's' : ''}` : 'No transactions detected');
        return;
      }
      if (failed > 0) toast.message(`${failed} of ${files.length} screenshot${files.length !== 1 ? 's' : ''} couldn't be read`);
      // Sort newest first for review
      merged.sort((a, b) => (b.date || '').localeCompare(a.date || ''));
      const today = new Date().toISOString().split('T')[0];
      const enriched = merged.map((t, idx) => ({
        _localId: `tx_${Date.now()}_${idx}`,
        ...t,
        date: t.date || today,
        family_amount: '', // blank = mirror real cost (t.amount)
        included: !t.is_duplicate,
      }));
      setScannedTransactions(enriched);
      setScanCardId('');
      setShowScanReview(true);
      toast.success(`Found ${enriched.length} charge${enriched.length !== 1 ? 's' : ''} across ${files.length} screenshot${files.length !== 1 ? 's' : ''}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to scan');
    } finally {
      setScanningStatement(false);
    }
  };
  
  const updateScannedRow = (localId, patch) => {
    setScannedTransactions(prev => prev.map(t => t._localId === localId ? { ...t, ...patch } : t));
  };
  
  const handleConfirmScannedBatch = async () => {
    const toSave = scannedTransactions.filter(t => t.included && parseFloat(t.amount) > 0 && (t.merchant || '').trim());
    if (toSave.length === 0) { toast.error('Nothing to save — check at least one row'); return; }
    try {
      setSavingScannedBatch(true);
      let savedCount = 0;
      for (const t of toSave) {
        try {
          const realAmount = parseFloat(t.amount) || 0;
          // Blank family_amount = mirror the real cost (no markup)
          const famRaw = (t.family_amount === '' || t.family_amount === null || t.family_amount === undefined)
            ? null
            : parseFloat(t.family_amount);
          const chargeAmount = (famRaw === null || isNaN(famRaw)) ? realAmount : famRaw;
          await axios.post(`${API}/api/moms-house/expenses`, {
            description: t.merchant.trim(),
            amount: realAmount,
            charge_amount: chargeAmount,
            date: t.date || new Date().toISOString().split('T')[0],
            notes: t.is_pending ? 'Pending (from statement scan)' : 'From statement scan',
            notify_contacts: [],
            card_used: scanCardId || '',
          });
          savedCount++;
        } catch (err) {
          console.error('Failed row', t, err);
        }
      }
      toast.success(`Added ${savedCount} of ${toSave.length} expense${toSave.length !== 1 ? 's' : ''}`);
      setShowScanReview(false);
      setShowScanConfirm(false);
      setScannedTransactions([]);
      // Refresh
      fetchExpenses();
      fetchCards();
    } catch (error) {
      toast.error('Failed to save batch');
    } finally {
      setSavingScannedBatch(false);
    }
  };
  
  // Quick "Add Payment" inline form
  const [addingPaymentId, setAddingPaymentId] = useState(null);
  const [addPaymentAmount, setAddPaymentAmount] = useState('');
  const [addPaymentCard, setAddPaymentCard] = useState('');
  
  // Card breakdown modal
  const [breakdownCard, setBreakdownCard] = useState(null); // card object
  const [breakdownData, setBreakdownData] = useState(null);
  const [loadingBreakdown, setLoadingBreakdown] = useState(false);
  
  const handleOpenCardBreakdown = async (card, scope = 'all', tabId = null) => {
    setBreakdownCard({ ...card, _scope: scope, _tab_id: tabId });
    setBreakdownData(null);
    try {
      setLoadingBreakdown(true);
      const params = new URLSearchParams({ scope });
      if (tabId) params.set('tab_id', tabId);
      const resp = await axios.get(`${API}/api/moms-house/cards/${card.id}/breakdown?${params.toString()}`);
      setBreakdownData(resp.data);
    } catch (error) {
      toast.error('Failed to load breakdown');
      setBreakdownCard(null);
    } finally {
      setLoadingBreakdown(false);
    }
  };
  
  const handleQuickAddPayment = async (expense) => {
    const add = parseFloat(addPaymentAmount);
    if (isNaN(add) || add <= 0) { toast.error('Enter a valid amount'); return; }
    if (!addPaymentCard) { toast.error('Pick which card was used'); return; }
    try {
      setSavingPaidId(expense.id);
      const resp = await axios.post(`${API}/api/moms-house/expenses/${expense.id}/payments`, {
        amount: add,
        date: new Date().toISOString().split('T')[0],
        card_used: addPaymentCard,
        notes: '',
        notify_contacts: [],
      });
      if (resp.data.success) {
        // Update local state with new totals + appended payment
        setExpenses(prev => prev.map(e => {
          if (e.id !== expense.id) return e;
          const updatedPayments = [...(e.payments || []), resp.data.payment];
          return {
            ...e,
            payments: updatedPayments,
            total_paid: resp.data.total_paid,
            paid_amount: resp.data.total_paid, // keep paid_amount in sync
            remaining_balance: resp.data.remaining_balance,
          };
        }));
        const cardName = cards.find(c => c.id === addPaymentCard)?.name || 'card';
        toast.success(`Added ${formatCurrency(add)} payment on ${cardName}`);
        fetchCards();
      }
      setAddingPaymentId(null);
      setAddPaymentAmount('');
      setAddPaymentCard('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add payment');
    } finally {
      setSavingPaidId(null);
    }
  };
  
  const handleSetPaidAmount = async (expenseId, paidAmount) => {
    try {
      setSavingPaidId(expenseId);
      const resp = await axios.put(`${API}/api/moms-house/expenses/${expenseId}`, { paid_amount: paidAmount });
      if (resp.data.success) {
        setExpenses(expenses.map(e => e.id === expenseId ? { ...e, ...resp.data.expense } : e));
        setPaidInputs(prev => { const next = { ...prev }; delete next[expenseId]; return next; });
        toast.success('Saved!');
        fetchCards();
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSavingPaidId(null);
    }
  };

  const handleStartEditRenovation = (exp) => {
    // The inline edit form is only rendered inside the cards view. If the user
    // clicked "edit" from compact/table view, switch them to cards so the form
    // becomes visible (otherwise the click silently does nothing).
    setRenovView('cards');
    setEditingRenovation(exp.id);
    setRenovationEditForm({
      description: exp.description || '',
      amount: exp.amount || 0,
      charge_amount: (exp.charge_amount === null || exp.charge_amount === undefined || exp.charge_amount === '') ? '' : exp.charge_amount,
      date: exp.date || '',
      notes: exp.notes || '',
      card_used: exp.card_used || ''
    });
    // Scroll the row into view so the user can immediately see the form.
    setTimeout(() => {
      const el = document.querySelector(`[data-testid="renovation-row-${exp.id}"]`);
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 80);
  };

  const handleSaveRenovationEdit = async (expenseId, options = {}) => {
    try {
      setSavingRenovation(true);
      const payload = {
        ...renovationEditForm,
        amount: parseFloat(renovationEditForm.amount) || 0,
      };
      // Family charge — if blank, mirror real cost
      if (renovationEditForm.charge_amount === '' || renovationEditForm.charge_amount === null) {
        payload.charge_amount = payload.amount;
      } else {
        payload.charge_amount = parseFloat(renovationEditForm.charge_amount) || 0;
      }
      if (options.paidInFull) {
        payload.paid_amount = payload.amount;
      }
      const response = await axios.put(`${API}/api/moms-house/expenses/${expenseId}`, payload);
      if (response.data.success) {
        setExpenses(expenses.map(e => e.id === expenseId ? { ...e, ...response.data.expense } : e));
        setEditingRenovation(null);
        toast.success(options.paidInFull ? 'Saved & marked Paid in Full!' : 'Saved!');
        fetchCards();
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSavingRenovation(false);
    }
  };
  
  const handleAddPayment = async (expenseId) => {
    const amount = parseFloat(newPayment.amount);
    if (!amount || amount <= 0) { toast.error('Enter a valid amount'); return; }
    try {
      setSavingPayment(true);
      const response = await axios.post(`${API}/api/moms-house/expenses/${expenseId}/payments`, {
        ...newPayment, amount, notify_contacts: paymentEmailContacts, card_used: newPayment.card_used
      });
      if (response.data.success) {
        // Update the expense in state
        setExpenses(expenses.map(e => {
          if (e.id === expenseId) {
            const payments = [...(e.payments || []), response.data.payment];
            return { ...e, payments, total_paid: response.data.total_paid, remaining_balance: response.data.remaining_balance };
          }
          return e;
        }));
        toast.success(`Payment of ${formatCurrency(amount)} recorded!`);
        if (response.data.emails_sent > 0) toast.success(`Notified ${response.data.emails_sent} family member(s)`);
        setNewPayment({ amount: '', date: new Date().toISOString().split('T')[0], notes: '', card_used: '' });
        setPaymentEmailContacts([]);
        setShowAddPayment(null);
        fetchCards();
      }
    } catch (error) {
      toast.error('Failed to add payment');
    } finally {
      setSavingPayment(false);
    }
  };
  
  const handleDeletePayment = async (expenseId, paymentId) => {
    if (!window.confirm('Delete this payment?')) return;
    try {
      const response = await axios.delete(`${API}/api/moms-house/expenses/${expenseId}/payments/${paymentId}`);
      if (response.data.success) {
        setExpenses(expenses.map(e => {
          if (e.id === expenseId) {
            const payments = (e.payments || []).filter(p => p.id !== paymentId);
            return { ...e, payments, total_paid: response.data.total_paid, remaining_balance: response.data.remaining_balance };
          }
          return e;
        }));
        toast.success('Payment removed');
        fetchCards();
      }
    } catch (error) {
      toast.error('Failed to delete');
    }
  };
  
  const handleUpdateRenovationExpense = async (expenseId, updates) => {
    try {
      setSavingRenovation(true);
      const response = await axios.put(`${API}/api/moms-house/expenses/${expenseId}`, updates);
      if (response.data.success) {
        setExpenses(expenses.map(e => e.id === expenseId ? { ...e, ...response.data.expense } : e));
        setEditingRenovation(null);
        toast.success('Updated!');
        fetchCards();
      }
    } catch (error) {
      toast.error('Failed to update');
    } finally {
      setSavingRenovation(false);
    }
  };
  
  const handleCopyLink = () => {
    const link = `${API}/api/moms-house/full-invoice`;
    navigator.clipboard.writeText(link);
    setCopiedLink('renovation-invoice');
    toast.success('Full invoice link copied! Paste in your group text.');
    setTimeout(() => setCopiedLink(null), 3000);
  };

  // Download a frozen-snapshot PDF of the family invoice (charge_amount only).
  // The PDF is stamped with the download date so it's a clean attachment for email.
  const handleDownloadInvoicePdf = async () => {
    try {
      setDownloadingPdf(true);
      const response = await axios.get(`${API}/api/moms-house/download-invoice-pdf`, {
        responseType: 'blob',
      });
      const today = new Date().toISOString().slice(0, 10);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `moms-house-invoice-${today}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success('Invoice PDF downloaded — ready to attach to your email.');
    } catch (err) {
      console.error(err);
      toast.error('Could not generate PDF');
    } finally {
      setDownloadingPdf(false);
    }
  };
  
  // Send full invoice email to selected contacts
  const [showEmailPicker, setShowEmailPicker] = useState(false);
  const [emailPickerContacts, setEmailPickerContacts] = useState([]);
  
  const handleSendInvoiceEmail = async () => {
    if (!emailPickerContacts.length) { toast.error('Select contacts first'); return; }
    try {
      setSendingEmail('invoice');
      const response = await axios.post(`${API}/api/moms-house/send-full-invoice`, { contact_ids: emailPickerContacts });
      if (response.data.success) {
        toast.success(`Full invoice emailed to ${response.data.emails_sent} member(s)!`);
        setShowEmailPicker(false);
        setEmailPickerContacts([]);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingEmail(null);
    }
  };
  
  const toggleContactSelection = (contactId) => {
    setSelectedContacts(prev => 
      prev.includes(contactId) ? prev.filter(id => id !== contactId) : [...prev, contactId]
    );
  };
  
  // Personal Tab Expenses
  const handleAddPersonalExpense = async () => {
    if (!newPersonalExpense.description.trim() || !newPersonalExpense.amount) {
      toast.error('Please enter description and amount');
      return;
    }
    const amount = parseFloat(newPersonalExpense.amount);
    if (isNaN(amount) || amount <= 0) { toast.error('Invalid amount'); return; }
    
    try {
      setSavingPersonalExpense(true);
      const response = await axios.post(`${API}/api/moms-house/personal-tabs/${activeTab}/expenses`, {
        ...newPersonalExpense, amount, card_used: newPersonalExpense.card_used
      });
      if (response.data.success) {
        toast.success(`Added: ${newPersonalExpense.description}`);
        setPersonalExpenses([response.data.expense, ...personalExpenses]);
        setPersonalTotal(prev => prev + amount);
        setNewPersonalExpense({ description: '', amount: '', date: new Date().toISOString().split('T')[0], category: '', notes: '', paid_by: '', card_used: '' });
        setShowAddPersonalExpense(false);
        fetchCards();
      }
    } catch (error) {
      toast.error('Failed to add expense');
    } finally {
      setSavingPersonalExpense(false);
    }
  };
  
  const handleDeletePersonalExpense = async (expenseId) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await axios.delete(`${API}/api/moms-house/personal-tabs/${activeTab}/expenses/${expenseId}`);
      const deleted = personalExpenses.find(e => e.id === expenseId);
      setPersonalExpenses(personalExpenses.filter(e => e.id !== expenseId));
      setPersonalTotal(prev => prev - (deleted?.amount || 0));
      toast.success('Deleted');
    } catch (error) {
      toast.error('Failed to delete');
    }
  };
  
  const handleEditExpense = (exp) => {
    setEditingExpense(exp.id);
    setEditForm({
      description: exp.description || '',
      amount: exp.amount || 0,
      date: exp.date || '',
      category: exp.category || '',
      notes: exp.notes || '',
      paid_by: exp.paid_by || '',
      card_used: exp.card_used || ''
    });
  };
  
  const handleSaveEdit = async (expenseId) => {
    try {
      setSavingEdit(true);
      const response = await axios.put(`${API}/api/moms-house/personal-tabs/${activeTab}/expenses/${expenseId}`, editForm);
      if (response.data.success) {
        const updated = response.data.expense;
        setPersonalExpenses(personalExpenses.map(e => e.id === expenseId ? { ...e, ...updated } : e));
        // Recalculate total
        const newTotal = personalExpenses.reduce((sum, e) => {
          if (e.id === expenseId) return sum + parseFloat(editForm.amount || 0);
          return sum + (e.amount || 0);
        }, 0);
        setPersonalTotal(newTotal);
        setEditingExpense(null);
        toast.success('Saved!');
        fetchCards();
      }
    } catch (error) {
      toast.error('Failed to save');
    } finally {
      setSavingEdit(false);
    }
  };
  
  const handleSaveTabEmail = async (tabId) => {
    try {
      await axios.put(`${API}/api/moms-house/personal-tabs/${tabId}`, { email: tabEmailInput });
      setPersonalTabs(personalTabs.map(t => t.id === tabId ? { ...t, email: tabEmailInput } : t));
      setEditingTabEmail(null);
      toast.success('Email saved');
    } catch (error) {
      toast.error('Failed to save email');
    }
  };
  
  const handleSendTabEmail = async (tabId) => {
    try {
      setSendingTabEmail(tabId);
      const response = await axios.post(`${API}/api/moms-house/personal-tabs/${tabId}/send-email`);
      if (response.data.success) toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingTabEmail(null);
    }
  };
  
  const handleCopyTabLink = (tab) => {
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/moms-house/tab/${tab.share_id}`;
    navigator.clipboard.writeText(link);
    setCopiedLink(tab.id);
    toast.success(`Link copied! Text this to ${tab.name}`);
    setTimeout(() => setCopiedLink(null), 3000);
  };
  
  // Helper: derive paid amount for an expense (uses paid_amount field or falls back to legacy payments sum)
  const getPaidAmount = (exp) => {
    // Per user request 2026-02-26: every entered expense is auto-paid in full.
    // If paid_amount is explicitly set OR payments array exists, use those.
    // Otherwise fall back to the full real cost so the UI never shows a zero
    // for a freshly entered expense.
    if (exp.paid_amount !== undefined && exp.paid_amount !== null && parseFloat(exp.paid_amount) > 0) {
      return parseFloat(exp.paid_amount) || 0;
    }
    const fromPayments = (exp.payments || []).reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
    if (fromPayments > 0) return fromPayments;
    return parseFloat(exp.amount) || 0;
  };
  
  const totalRenovationsBilled = expenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);
  const totalFamilyCharge = expenses.reduce((sum, exp) => {
    const ca = exp.charge_amount;
    return sum + (ca === null || ca === undefined || ca === '' ? (parseFloat(exp.amount) || 0) : (parseFloat(ca) || 0));
  }, 0);
  const totalRenovationsPaid = expenses.reduce((sum, exp) => sum + getPaidAmount(exp), 0);
  const totalRenovations = totalRenovationsPaid;
  // Per-card Family-Charge total (client-side derived from charge_amount of each expense
  // attributed to that card via payments[] or expense.card_used). This mirrors what the
  // backend computes for `breakdown.renovations` but using charge_amount instead of amount.
  const familyChargeByCard = React.useMemo(() => {
    const map = {};
    expenses.forEach(exp => {
      const charge = (exp.charge_amount === null || exp.charge_amount === undefined || exp.charge_amount === '')
        ? (parseFloat(exp.amount) || 0)
        : (parseFloat(exp.charge_amount) || 0);
      const realAmt = parseFloat(exp.amount) || 0;
      if (realAmt <= 0 || charge <= 0) return;
      const ratio = charge / realAmt; // if family is charged 8000 for 5000 of work, ratio=1.6
      const payments = exp.payments || [];
      if (payments.length > 0) {
        payments.forEach(p => {
          const cid = (p.card_used || exp.card_used || '').trim();
          if (!cid) return;
          map[cid] = (map[cid] || 0) + (parseFloat(p.amount) || 0) * ratio;
        });
      } else {
        // No payments yet — attribute the whole charge to the expense's card if set
        const cid = (exp.card_used || '').trim();
        if (cid) map[cid] = (map[cid] || 0) + charge;
      }
    });
    return map;
  }, [expenses]);

  // Bulk-select for renovation expenses (cards/compact/table all share)
  const [bulkMode, setBulkMode] = React.useState(false);
  const [bulkSelected, setBulkSelected] = React.useState(new Set());
  const toggleBulkSelect = (id) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllVisible = () => setBulkSelected(new Set(expenses.map(e => e.id)));
  const clearBulkSelection = () => { setBulkSelected(new Set()); };
  const exitBulkMode = () => { setBulkMode(false); setBulkSelected(new Set()); };
  const handleBulkDelete = async () => {
    if (bulkSelected.size === 0) return;
    if (!window.confirm(`Delete ${bulkSelected.size} expense${bulkSelected.size === 1 ? '' : 's'}? This cannot be undone.`)) return;
    const ids = Array.from(bulkSelected);
    let okCount = 0; let failCount = 0;
    await Promise.all(ids.map(async id => {
      try {
        const r = await axios.delete(`${API}/api/moms-house/expenses/${id}`);
        if (r.data?.success !== false) okCount++; else failCount++;
      } catch { failCount++; }
    }));
    setExpenses(prev => prev.filter(e => !bulkSelected.has(e.id)));
    fetchCards();
    if (failCount === 0) toast.success(`Deleted ${okCount} expense${okCount === 1 ? '' : 's'}`);
    else toast.error(`Deleted ${okCount}, failed ${failCount}`);
    exitBulkMode();
  };

  // ── Generic bulk-select for OTHER sections (Personal Tabs, Payments, Contacts) ──
  // Each section gets its own scope so selections don't bleed across tabs.
  const [bulkScope, setBulkScope] = React.useState(null); // 'personal-expenses' | 'personal-payments' | 'contacts' | null
  const [bulkScopeIds, setBulkScopeIds] = React.useState(new Set());
  const inScope = (scope) => bulkScope === scope;
  const enterScope = (scope) => { setBulkScope(scope); setBulkScopeIds(new Set()); };
  const exitScope = () => { setBulkScope(null); setBulkScopeIds(new Set()); };
  const toggleScopeId = (id) => {
    setBulkScopeIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const selectAllInScope = (allIds) => setBulkScopeIds(new Set(allIds));
  const clearScopeSelection = () => setBulkScopeIds(new Set());

  const handleBulkDeletePersonalExpenses = async () => {
    if (bulkScopeIds.size === 0) return;
    if (!window.confirm(`Delete ${bulkScopeIds.size} expense${bulkScopeIds.size === 1 ? '' : 's'}? This cannot be undone.`)) return;
    const ids = Array.from(bulkScopeIds);
    let ok = 0, fail = 0;
    await Promise.all(ids.map(async id => {
      try {
        await axios.delete(`${API}/api/moms-house/personal-tabs/${activeTab}/expenses/${id}`);
        ok++;
      } catch { fail++; }
    }));
    setPersonalExpenses(prev => prev.filter(e => !bulkScopeIds.has(e.id)));
    fetchCards();
    fail === 0 ? toast.success(`Deleted ${ok}`) : toast.error(`Deleted ${ok}, failed ${fail}`);
    exitScope();
  };

  const handleBulkDeletePersonalPayments = async () => {
    if (bulkScopeIds.size === 0) return;
    if (!window.confirm(`Delete ${bulkScopeIds.size} payment${bulkScopeIds.size === 1 ? '' : 's'}?`)) return;
    const ids = Array.from(bulkScopeIds);
    let ok = 0, fail = 0;
    await Promise.all(ids.map(async id => {
      try {
        await axios.delete(`${API}/api/moms-house/personal-tabs/${activeTab}/payments/${id}`);
        ok++;
      } catch { fail++; }
    }));
    setPersonalPayments(prev => prev.filter(p => !bulkScopeIds.has(p.id)));
    fail === 0 ? toast.success(`Deleted ${ok}`) : toast.error(`Deleted ${ok}, failed ${fail}`);
    exitScope();
  };

  const handleBulkDeleteContacts = async () => {
    if (bulkScopeIds.size === 0) return;
    if (!window.confirm(`Delete ${bulkScopeIds.size} contact${bulkScopeIds.size === 1 ? '' : 's'}? They will stop receiving invoices.`)) return;
    const ids = Array.from(bulkScopeIds);
    let ok = 0, fail = 0;
    await Promise.all(ids.map(async id => {
      try {
        await axios.delete(`${API}/api/moms-house/contacts/${id}`);
        ok++;
      } catch { fail++; }
    }));
    setContacts(prev => prev.filter(c => !bulkScopeIds.has(c.id)));
    fail === 0 ? toast.success(`Deleted ${ok}`) : toast.error(`Deleted ${ok}, failed ${fail}`);
    exitScope();
  };

  React.useEffect(() => {
    // Clear any bulk selection when switching between tabs so selections don't bleed
    setBulkScope(null);
    setBulkScopeIds(new Set());
    setBulkMode(false);
    setBulkSelected(new Set());
  }, [activeTab]);

  // Tiny reusable bulk-select toolbar component
  const BulkBar = ({ scope, allIds, count, onDelete, label = 'item' }) => (
    !inScope(scope) ? (
      <Button size="sm" variant="outline" onClick={() => enterScope(scope)}
        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-7 px-2.5 text-[11px]"
        data-testid={`enter-bulk-${scope}-btn`}>
        <Trash2 className="w-3 h-3 mr-1" /> Select multiple
      </Button>
    ) : (
      <div className="flex items-center gap-1.5 flex-wrap">
        <span className="text-pink-300 text-[11px] font-semibold">{bulkScopeIds.size} selected</span>
        <Button size="sm" onClick={() => selectAllInScope(allIds)}
          className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 h-7 px-2 text-[11px]" data-testid={`bulk-${scope}-all-btn`}>All</Button>
        <Button size="sm" onClick={clearScopeSelection}
          className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 h-7 px-2 text-[11px]" data-testid={`bulk-${scope}-clear-btn`}>Clear</Button>
        <Button size="sm" onClick={onDelete} disabled={bulkScopeIds.size === 0}
          className="bg-red-600 hover:bg-red-700 text-white h-7 px-2.5 text-[11px] disabled:opacity-40" data-testid={`bulk-${scope}-delete-btn`}>
          <Trash2 className="w-3 h-3 mr-1" /> Delete {bulkScopeIds.size > 0 ? `(${bulkScopeIds.size})` : ''}
        </Button>
        <Button size="sm" variant="outline" onClick={exitScope}
          className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 h-7 px-2 text-[11px]" data-testid={`exit-bulk-${scope}-btn`}>
          <X className="w-3 h-3" />
        </Button>
      </div>
    )
  );

  // Renovation amount perspective: 'mine' (real cost) vs 'family' (charged) — local-only
  const [renovPerspective, setRenovPerspective] = React.useState(() => localStorage.getItem('momsHouseRenovPerspective') || 'mine');
  React.useEffect(() => { localStorage.setItem('momsHouseRenovPerspective', renovPerspective); }, [renovPerspective]);
  // Helper: amount visible for current perspective
  const displayAmount = React.useCallback((exp) => {
    if (renovPerspective === 'family') {
      const ca = exp.charge_amount;
      return ca === null || ca === undefined || ca === '' ? (parseFloat(exp.amount) || 0) : (parseFloat(ca) || 0);
    }
    return parseFloat(exp.amount) || 0;
  }, [renovPerspective]);
  const formatCurrency = (amount) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
  const formatDate = (dateStr) => new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  
  const currentPersonalTab = personalTabs.find(t => t.id === activeTab);

  return (
    <div className="min-h-screen bg-zinc-950 p-4 md:p-8">
      <MobileNav />
      
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Button variant="ghost" onClick={() => navigate('/')} className="text-zinc-400 hover:text-white">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-xl flex items-center justify-center">
              <Home className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-xl md:text-2xl font-bold text-white">Mom's House</h1>
              <p className="text-sm text-zinc-400">Renovation & Personal Tabs</p>
            </div>
          </div>
        </div>
        
        {/* Main Tabs */}
        <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1" data-testid="moms-house-tabs">
          <button
            onClick={() => setActiveTab('renovations')}
            data-testid="tab-renovations"
            className={`px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap flex items-center gap-1.5 transition-all ${
              activeTab === 'renovations' ? 'bg-pink-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <Home className="w-4 h-4" />
            House Renovations
          </button>
          {personalTabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              data-testid={`tab-${tab.name.toLowerCase()}`}
              className={`px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap flex items-center gap-1.5 transition-all ${
                activeTab === tab.id ? 'bg-purple-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              <User className="w-4 h-4" />
              {tab.name}
            </button>
          ))}
          <button
            onClick={() => setActiveTab('cardpayback')}
            data-testid="tab-cardpayback"
            className={`px-4 py-2.5 rounded-lg font-medium text-sm whitespace-nowrap flex items-center gap-1.5 transition-all ${
              activeTab === 'cardpayback' ? 'bg-rose-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
            }`}
          >
            <CreditCard className="w-4 h-4" />
            Credit Card Payback
          </button>
          {/* Contacts tab removed 2026-02-08 per user request. */}
          {/* Contractors tab removed per user request 2026-02-26 */}
        </div>

        {/* ════════════════════════════════════════════ */}
        {/* HOUSE RENOVATIONS TAB (Shared - Everyone) */}
        {/* ════════════════════════════════════════════ */}
        {activeTab === 'renovations' && (
          <div className="space-y-4">
            {/* Total Banner */}
            <Card className={`border-2 transition-all ${renovPerspective === 'family' ? 'bg-gradient-to-r from-pink-900/50 to-rose-900/50 border-pink-500/30' : 'bg-gradient-to-r from-emerald-900/40 to-emerald-950/50 border-emerald-500/30'}`}>
              <CardContent className="p-5">
                {/* Perspective Toggle */}
                <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                  <div className="inline-flex bg-zinc-900/80 border border-zinc-700 rounded-lg p-0.5" role="tablist">
                    <button onClick={() => setRenovPerspective('mine')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${renovPerspective === 'mine' ? 'bg-emerald-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                      data-testid="perspective-mine-btn">
                      <Lock className="w-3.5 h-3.5" /> My View <span className="text-[10px] opacity-75">(real cost)</span>
                    </button>
                    <button onClick={() => setRenovPerspective('family')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${renovPerspective === 'family' ? 'bg-pink-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                      data-testid="perspective-family-btn">
                      <Mail className="w-3.5 h-3.5" /> Family Invoice
                    </button>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wide font-semibold ${renovPerspective === 'family' ? 'text-pink-300/80' : 'text-emerald-300/80'}`}>
                    {renovPerspective === 'family' ? 'What family sees' : 'Private — only you'}
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div>
                    <p className={`${renovPerspective === 'family' ? 'text-pink-300' : 'text-emerald-300'} text-sm font-medium`}>
                      {renovPerspective === 'family' ? 'Total Family Charge' : 'My Real Cost'}
                    </p>
                    <p className="text-3xl font-bold text-white mt-1" data-testid="total-paid-display">
                      {formatCurrency(renovPerspective === 'family' ? totalFamilyCharge : totalRenovationsBilled)}
                    </p>
                    <p className={`${renovPerspective === 'family' ? 'text-pink-300/70' : 'text-emerald-300/70'} text-xs mt-1`}>
                      {renovPerspective === 'family'
                        ? <>Markup: {formatCurrency(totalFamilyCharge - totalRenovationsBilled)} • {expenses.length} expense{expenses.length !== 1 ? 's' : ''}</>
                        : <>Paid So Far: {formatCurrency(totalRenovationsPaid)} • {expenses.length} expense{expenses.length !== 1 ? 's' : ''}</>}
                    </p>
                  </div>
                  {expenses.length > 0 && (
                    <div className="flex flex-col gap-2 items-end">
                      <Button size="sm" onClick={() => {
                        setEmailPickerContacts(contacts.map(c => c.id));
                        setShowEmailPicker(true);
                      }}
                        className="bg-pink-600 hover:bg-pink-700 text-white text-xs" data-testid="email-full-invoice-btn">
                        {sendingEmail === 'invoice' ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                        Email Full Invoice
                      </Button>
                      <Button size="sm" onClick={handleDownloadInvoicePdf} disabled={downloadingPdf}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs disabled:opacity-50" data-testid="download-invoice-pdf-btn">
                        {downloadingPdf ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <FileText className="w-3 h-3 mr-1" />}
                        Download PDF Invoice
                      </Button>
                      <Button size="sm" variant="outline" onClick={handleCopyLink}
                        className={`text-xs ${copiedLink === 'renovation-invoice' ? 'bg-green-600 border-green-600 text-white' : 'border-pink-500/50 text-pink-300 hover:bg-pink-800'}`}
                        data-testid="copy-invoice-link-btn">
                        {copiedLink === 'renovation-invoice' ? <><Check className="w-3 h-3 mr-1" /> Copied!</> : <><Copy className="w-3 h-3 mr-1" /> Copy Invoice Link</>}
                      </Button>
                    </div>
                  )}
                </div>

                {/* Email Picker - shown as overlay so it's always visible on mobile */}

                {/* 2026-02-27 — My View vs Family View comparison strip.
                    Shows the exact $ difference so the user knows their real
                    cost vs what the family will be charged.  Only visible in
                    "My View" (never rendered into the invoice PDF, and never
                    shown when the Family Invoice tab is selected). */}
                {renovPerspective === 'mine' && expenses.length > 0 && (() => {
                  const mineTotal = totalRenovationsBilled;
                  const familyTotal = totalFamilyCharge;
                  const diff = familyTotal - mineTotal;
                  const pct = mineTotal > 0 ? (diff / mineTotal) * 100 : 0;
                  return (
                    <div
                      className="mt-4 rounded-lg border border-emerald-500/25 bg-black/30 p-3"
                      data-testid="mine-vs-family-comparison"
                    >
                      <div className="flex items-center gap-2 mb-2">
                        <Lock className="w-3.5 h-3.5 text-emerald-400" />
                        <span className="text-[11px] uppercase tracking-wide font-semibold text-emerald-300/90">
                          My View vs Family View · private
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-md bg-emerald-500/10 border border-emerald-500/30 py-2 px-1">
                          <p className="text-[10px] uppercase text-emerald-300/70 tracking-wide">My Real Cost</p>
                          <p className="text-white font-bold text-sm mt-0.5" data-testid="mine-total">
                            {formatCurrency(mineTotal)}
                          </p>
                        </div>
                        <div className="rounded-md bg-pink-500/10 border border-pink-500/30 py-2 px-1">
                          <p className="text-[10px] uppercase text-pink-300/70 tracking-wide">Family Will Pay</p>
                          <p className="text-white font-bold text-sm mt-0.5" data-testid="family-total">
                            {formatCurrency(familyTotal)}
                          </p>
                        </div>
                        <div className={`rounded-md py-2 px-1 border ${diff >= 0 ? 'bg-amber-500/10 border-amber-500/40' : 'bg-rose-500/10 border-rose-500/40'}`}>
                          <p className={`text-[10px] uppercase tracking-wide ${diff >= 0 ? 'text-amber-300/80' : 'text-rose-300/80'}`}>
                            {diff >= 0 ? 'Difference' : 'Under'}
                          </p>
                          <p className={`font-bold text-sm mt-0.5 ${diff >= 0 ? 'text-amber-200' : 'text-rose-200'}`} data-testid="mine-family-diff">
                            {diff >= 0 ? '+' : ''}{formatCurrency(diff)}
                          </p>
                          {mineTotal > 0 && (
                            <p className={`text-[10px] mt-0.5 ${diff >= 0 ? 'text-amber-300/70' : 'text-rose-300/70'}`}>
                              {diff >= 0 ? '+' : ''}{pct.toFixed(1)}%
                            </p>
                          )}
                        </div>
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-2 italic">
                        Never printed on the invoice — you&apos;re the only one who sees this.
                      </p>
                    </div>
                  );
                })()}
              </CardContent>
            </Card>

            {/* Card Breakdown Modal */}
            {breakdownCard && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setBreakdownCard(null)}>
                <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-lg max-h-[85vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                  <div className="sticky top-0 bg-zinc-900 border-b border-zinc-700 p-4 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <CreditCard className="w-5 h-5" style={{ color: breakdownCard.color }} />
                      <div>
                        <h3 className="text-white font-semibold">{breakdownCard.name}</h3>
                        <p className="text-zinc-400 text-xs">
                          {breakdownCard._scope === 'renovations' ? 'House Renovations only'
                            : breakdownCard._scope === 'personal' ? `Personal tab only`
                            : breakdownCard._scope === 'contractor' ? 'Contractors only'
                            : 'Spending breakdown'}
                        </p>
                      </div>
                    </div>
                    <button onClick={() => setBreakdownCard(null)} className="text-zinc-400 hover:text-white" data-testid="breakdown-close-btn"><X className="w-5 h-5" /></button>
                  </div>
                  <div className="p-4">
                    {loadingBreakdown ? (
                      <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-pink-400 mb-2" /><p className="text-zinc-400 text-sm">Loading...</p></div>
                    ) : breakdownData && breakdownData.items && breakdownData.items.length > 0 ? (
                      <>
                        <div className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3 mb-4 flex items-center justify-between">
                          <span className="text-zinc-300 text-sm font-medium">Total Spent</span>
                          <span className="text-white text-xl font-bold">{formatCurrency(breakdownData.total_spent || 0)}</span>
                        </div>
                        <div className="space-y-2" data-testid="breakdown-items">
                          {breakdownData.items.map((item, idx) => {
                            const typeLabel = item.type === 'renovation_payment' ? 'Renovation'
                              : item.type === 'personal' ? (item.tab_name || 'Personal')
                              : item.type === 'contractor_material' ? 'Materials'
                              : item.type === 'contractor_labor' ? 'Labor'
                              : 'Charge';
                            const typeStyle = item.type === 'renovation_payment' ? 'bg-pink-900/50 text-pink-300'
                              : item.type === 'personal' ? 'bg-purple-900/50 text-purple-300'
                              : 'bg-amber-900/50 text-amber-300';
                            return (
                              <div key={`${item.type}-${item.id}-${idx}`} className="bg-zinc-800/50 border border-zinc-700 rounded-lg p-3">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 flex-wrap">
                                      <h4 className="text-white text-sm font-semibold truncate">{item.description}</h4>
                                      <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${typeStyle}`}>{typeLabel}</span>
                                      {item.default_white && (
                                        <span className="px-1.5 py-0.5 rounded-full text-[9px] font-medium bg-zinc-700 text-zinc-300">no card → White</span>
                                      )}
                                    </div>
                                    <div className="flex items-center gap-2 mt-1 text-xs text-zinc-500 flex-wrap">
                                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {item.date ? formatDate(item.date) : 'No date'}</span>
                                      {item.method && <span className="text-zinc-400">{item.method}</span>}
                                      {item.notes && <span className="text-zinc-400 truncate">- {item.notes}</span>}
                                    </div>
                                  </div>
                                  <span className="text-green-400 font-bold text-base flex-shrink-0">{formatCurrency(item.amount_paid)}</span>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </>
                    ) : (
                      <div className="text-center py-8">
                        <CreditCard className="w-10 h-10 mx-auto text-zinc-600 mb-2" />
                        <p className="text-zinc-400 text-sm">Nothing has been charged to this card yet.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
            

            {/* Statement Scan Review Modal */}
            {showScanReview && (
              <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-50 p-2 sm:p-4" onClick={() => !savingScannedBatch && setShowScanReview(false)}>
                <div className="bg-zinc-900 border border-zinc-700 rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
                  <div className="sticky top-0 bg-gradient-to-r from-purple-700 to-pink-700 px-4 py-3 flex items-center justify-between z-10">
                    <div className="flex items-center gap-2 min-w-0">
                      <Camera className="w-5 h-5 text-white flex-shrink-0" />
                      <div className="min-w-0">
                        <h3 className="text-white font-semibold text-sm sm:text-base truncate">Review Scanned Transactions</h3>
                        <p className="text-purple-100 text-xs">Edit, exclude, then save</p>
                      </div>
                    </div>
                    <button onClick={() => setShowScanReview(false)} disabled={savingScannedBatch} className="p-1.5 hover:bg-white/20 rounded text-white" data-testid="scan-review-close">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  
                  <div className="p-4 space-y-3">
                    {/* Card selector for batch */}
                    {cards.length > 0 && (
                      <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-3">
                        <Label className="text-zinc-300 text-xs mb-2 block">Apply Card to All (optional)</Label>
                        <div className="flex flex-wrap gap-1.5">
                          {cards.map(card => (
                            <button key={card.id} onClick={() => setScanCardId(scanCardId === card.id ? '' : card.id)}
                              className={`px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 transition-all ${
                                scanCardId === card.id ? 'border-pink-500 bg-pink-600/20 text-white' : 'border-zinc-600 bg-zinc-900 text-zinc-400 hover:bg-zinc-800'
                              }`} data-testid={`scan-card-${card.id}`}>
                              <span className="w-3 h-2 rounded-sm" style={{ backgroundColor: card.color, border: card.id === 'white' ? '1px solid #666' : 'none' }} />
                              {card.name}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Summary */}
                    <div className="flex items-center justify-between bg-zinc-800/40 border border-zinc-700 rounded-lg px-3 py-2 text-xs flex-wrap gap-2">
                      <span className="text-zinc-400">{scannedTransactions.filter(t => t.included).length} of {scannedTransactions.length} selected</span>
                      <div className="flex items-center gap-2">
                        <button onClick={() => setScannedTransactions(prev => prev.map(t => ({ ...t, included: true })))} className="text-pink-400 hover:underline" data-testid="scan-select-all">Select all</button>
                        <span className="text-zinc-700">·</span>
                        <button onClick={() => setScannedTransactions(prev => prev.map(t => ({ ...t, included: !t.is_duplicate })))} className="text-amber-400 hover:underline">Skip duplicates</button>
                      </div>
                    </div>
                    
                    {/* Transactions list */}
                    <div className="space-y-2">
                      {scannedTransactions.length === 0 && (
                        <p className="text-zinc-500 text-xs text-center py-3">All rows removed. Use "Add Row" below or close this modal.</p>
                      )}
                      {scannedTransactions.map((t) => (
                        <div key={t._localId} className={`border rounded-lg p-3 transition-colors ${
                          !t.included ? 'bg-zinc-900/40 border-zinc-800 opacity-60' :
                          t.is_duplicate ? 'bg-amber-950/30 border-amber-700/50' :
                          'bg-zinc-800/60 border-zinc-700'
                        }`} data-testid={`scan-row-${t._localId}`}>
                          <div className="flex items-start gap-2">
                            <input type="checkbox" checked={t.included} onChange={(e) => updateScannedRow(t._localId, { included: e.target.checked })}
                              className="w-5 h-5 mt-1 rounded border-zinc-600 bg-zinc-900 cursor-pointer flex-shrink-0" data-testid={`scan-row-include-${t._localId}`} />
                            <div className="flex-1 min-w-0 space-y-2">
                              {t.is_duplicate && (
                                <div className="flex items-start gap-1.5 text-amber-300 text-xs bg-amber-950/40 border border-amber-700/40 rounded px-2 py-1.5">
                                  <AlertTriangle className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" />
                                  <span>Looks like a duplicate of <strong>{t.duplicate_of?.description}</strong> on {t.duplicate_of?.date}. Are you sure?</span>
                                </div>
                              )}
                              {t.is_pending && !t.is_duplicate && (
                                <div className="text-amber-400 text-[10px] uppercase font-semibold">Pending</div>
                              )}
                              <div className="grid grid-cols-1 sm:grid-cols-[1fr_120px_130px_130px] gap-2">
                                <Input value={t.merchant} onChange={(e) => updateScannedRow(t._localId, { merchant: e.target.value })}
                                  placeholder="Merchant" className="bg-zinc-900 border-zinc-600 text-white h-9 text-sm" data-testid={`scan-row-merchant-${t._localId}`} />
                                <Input type="date" value={t.date} onChange={(e) => updateScannedRow(t._localId, { date: e.target.value })}
                                  className="bg-zinc-900 border-zinc-600 text-white h-9 text-sm" data-testid={`scan-row-date-${t._localId}`} />
                                <div className="relative" title="What you actually paid out of pocket">
                                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-emerald-500" />
                                  <Input type="number" step="0.01" value={t.amount} onChange={(e) => updateScannedRow(t._localId, { amount: e.target.value })}
                                    placeholder="My real cost"
                                    className="bg-zinc-900 border-emerald-700/40 text-white h-9 text-sm pl-7" data-testid={`scan-row-amount-${t._localId}`} />
                                </div>
                                <div className="relative" title="What the family is being charged (blank = same as real cost)">
                                  <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-pink-500" />
                                  <Input type="number" step="0.01" value={t.family_amount ?? ''} onChange={(e) => updateScannedRow(t._localId, { family_amount: e.target.value })}
                                    placeholder={t.amount ? `Family (${t.amount})` : 'Family $'}
                                    className="bg-zinc-900 border-pink-700/40 text-white h-9 text-sm pl-7" data-testid={`scan-row-family-amount-${t._localId}`} />
                                </div>
                              </div>
                              <div className="flex items-center justify-between text-[10px] text-zinc-500 mt-1 px-0.5">
                                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500/70" /> My real cost</span>
                                <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-pink-500/70" /> Family charge — blank = same as real cost</span>
                              </div>
                            </div>
                            <button
                              onClick={() => setScannedTransactions(prev => prev.filter(x => x._localId !== t._localId))}
                              className="p-1.5 mt-0.5 text-zinc-500 hover:text-red-400 hover:bg-red-500/10 rounded flex-shrink-0"
                              title="Delete row"
                              data-testid={`scan-delete-${t._localId}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      ))}
                      
                      {/* Add manual row */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setScannedTransactions(prev => [
                          ...prev,
                          { _localId: `tx_manual_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`, merchant: '', amount: '', family_amount: '', date: new Date().toISOString().split('T')[0], is_pending: false, is_duplicate: false, included: true },
                        ])}
                        className="w-full bg-zinc-800 border-zinc-600 text-zinc-300 hover:bg-zinc-700 h-10"
                        data-testid="scan-add-row"
                      >
                        <Plus className="w-4 h-4 mr-1" /> Add Row Manually
                      </Button>
                    </div>
                  </div>
                  
                  {/* Footer */}
                  <div className="sticky bottom-0 bg-zinc-900 border-t border-zinc-700 p-3 flex flex-wrap gap-2 justify-between items-center">
                    <div className="text-xs text-zinc-400">
                      Total: <span className="text-pink-400 font-bold">${scannedTransactions.filter(t => t.included).reduce((s, t) => s + (parseFloat(t.amount) || 0), 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" onClick={() => setShowScanReview(false)} disabled={savingScannedBatch} className="text-zinc-400">Cancel</Button>
                      <Button onClick={() => setShowScanConfirm(true)} disabled={savingScannedBatch || scannedTransactions.filter(t => t.included && parseFloat(t.amount) > 0 && (t.merchant || '').trim()).length === 0}
                        className="bg-purple-600 hover:bg-purple-700 disabled:opacity-40 text-white" data-testid="scan-review-continue">
                        Continue → Final Review
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Final Confirmation Modal */}
            {showScanConfirm && (
              <div className="fixed inset-0 bg-black/85 backdrop-blur-sm flex items-center justify-center z-[60] p-4" onClick={() => !savingScannedBatch && setShowScanConfirm(false)}>
                <div className="bg-zinc-900 border border-pink-500/50 rounded-xl w-full max-w-md shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
                  <div className="bg-gradient-to-r from-pink-600 to-rose-600 px-4 py-3 flex items-center gap-2">
                    <Check className="w-5 h-5 text-white" />
                    <h3 className="text-white font-semibold">Final Confirmation</h3>
                  </div>
                  <div className="p-4 space-y-3">
                    <p className="text-zinc-300 text-sm">You're about to add the following to your renovation ledger:</p>
                    <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 space-y-1.5 max-h-[40vh] overflow-y-auto">
                      <div className="flex items-center justify-between text-[10px] uppercase tracking-wide text-zinc-500 pb-1 border-b border-zinc-700/60">
                        <span className="flex-1">Merchant</span>
                        <span className="w-16 text-center">Date</span>
                        <span className="w-20 text-right text-emerald-400">My cost</span>
                        <span className="w-20 text-right text-pink-400">Family</span>
                      </div>
                      {scannedTransactions.filter(t => t.included).map(t => {
                        const real = parseFloat(t.amount) || 0;
                        const famRaw = t.family_amount;
                        const fam = (famRaw === '' || famRaw === null || famRaw === undefined || isNaN(parseFloat(famRaw))) ? real : parseFloat(famRaw);
                        const markup = fam !== real;
                        return (
                          <div key={t._localId} className="flex items-center justify-between text-xs gap-2">
                            <span className="text-white truncate flex-1" title={t.merchant}>{t.merchant}</span>
                            <span className="text-zinc-500 w-16 text-center text-[11px]">{t.date}</span>
                            <span className="text-emerald-400 font-semibold w-20 text-right">${real.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                            <span className={`font-semibold w-20 text-right ${markup ? 'text-pink-400' : 'text-zinc-500'}`} title={markup ? 'Family is being charged a different amount' : 'Same as real cost'}>
                              ${fam.toLocaleString('en-US', { minimumFractionDigits: 2 })}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                    {(() => {
                      const rows = scannedTransactions.filter(t => t.included);
                      const realTotal = rows.reduce((s, t) => s + (parseFloat(t.amount) || 0), 0);
                      const famTotal = rows.reduce((s, t) => {
                        const real = parseFloat(t.amount) || 0;
                        const famRaw = t.family_amount;
                        const fam = (famRaw === '' || famRaw === null || famRaw === undefined || isNaN(parseFloat(famRaw))) ? real : parseFloat(famRaw);
                        return s + fam;
                      }, 0);
                      return (
                        <div className="space-y-1.5">
                          <div className="flex items-center justify-between bg-emerald-600/10 border border-emerald-600/30 rounded-lg px-3 py-2">
                            <span className="text-emerald-300 text-sm font-medium">My real out-of-pocket</span>
                            <span className="text-white font-bold">${realTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          </div>
                          <div className="flex items-center justify-between bg-pink-600/10 border border-pink-600/30 rounded-lg px-3 py-2">
                            <span className="text-pink-300 text-sm font-medium">Family is charged</span>
                            <span className="text-white font-bold">${famTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
                          </div>
                          {famTotal !== realTotal && (
                            <div className="flex items-center justify-between text-[11px] px-1">
                              <span className="text-zinc-500">Markup / discount</span>
                              <span className={famTotal > realTotal ? 'text-pink-300 font-semibold' : 'text-amber-300 font-semibold'}>
                                {famTotal > realTotal ? '+' : ''}${(famTotal - realTotal).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {scanCardId && (
                      <p className="text-zinc-400 text-xs">Card: <span className="text-white font-medium">{cards.find(c => c.id === scanCardId)?.name || scanCardId}</span></p>
                    )}
                    <div className="flex gap-2 pt-2">
                      <Button onClick={handleConfirmScannedBatch} disabled={savingScannedBatch} className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="scan-final-confirm">
                        {savingScannedBatch ? <><RefreshCw className="w-4 h-4 mr-1 animate-spin" /> Saving...</> : <><Check className="w-4 h-4 mr-1" /> Yes, Add Them All</>}
                      </Button>
                      <Button variant="ghost" onClick={() => setShowScanConfirm(false)} disabled={savingScannedBatch} className="text-zinc-400">Back</Button>
                    </div>
                  </div>
                </div>
              </div>
            )}


            {/* Email Full Invoice Modal */}
            {showEmailPicker && (
              <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
                <div className="bg-zinc-900 border border-zinc-700 rounded-xl p-5 w-full max-w-sm shadow-2xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-white font-semibold flex items-center gap-2"><Mail className="w-4 h-4 text-pink-400" /> Email Full Invoice</h3>
                    <button onClick={() => { setShowEmailPicker(false); setEmailPickerContacts([]); }} className="text-zinc-400 hover:text-white"><X className="w-5 h-5" /></button>
                  </div>
                  <p className="text-zinc-400 text-sm mb-3">Select who to send the full renovation invoice to:</p>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {contacts.map(c => (
                      <button key={c.id} onClick={() => setEmailPickerContacts(prev => prev.includes(c.id) ? prev.filter(id => id !== c.id) : [...prev, c.id])}
                        className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 transition-all ${
                          emailPickerContacts.includes(c.id) ? 'bg-pink-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                        }`}>
                        {emailPickerContacts.includes(c.id) && <Check className="w-3 h-3" />} {c.name}
                      </button>
                    ))}
                    <button onClick={() => setEmailPickerContacts(contacts.map(c => c.id))} className="px-3 py-2 rounded-lg text-sm font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">Select All</button>
                  </div>
                  {emailPickerContacts.length > 0 && (
                    <p className="text-pink-400 text-xs mb-3">{emailPickerContacts.length} contact{emailPickerContacts.length !== 1 ? 's' : ''} selected</p>
                  )}
                  <div className="flex gap-3">
                    <button onClick={handleSendInvoiceEmail} disabled={!emailPickerContacts.length || sendingEmail === 'invoice'}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-pink-600 hover:bg-pink-700 disabled:bg-zinc-700 disabled:text-zinc-500 rounded-lg text-white font-semibold transition-colors">
                      {sendingEmail === 'invoice' ? <><RefreshCw className="w-4 h-4 animate-spin" /> Sending...</> : <><Send className="w-4 h-4" /> Send Invoice</>}
                    </button>
                    <button onClick={() => { setShowEmailPicker(false); setEmailPickerContacts([]); }} className="px-4 py-3 bg-zinc-700 hover:bg-zinc-600 rounded-lg text-white transition-colors">Cancel</button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Credit Card Balances — scoped to RENOVATIONS only */}
            {cards.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="card-balances-grid">
                {cards.map(card => {
                  const realRenov = card?.breakdown?.renovations ?? 0;
                  const familyRenov = familyChargeByCard[card.id] ?? 0;
                  const renovSpent = renovPerspective === 'family' ? familyRenov : realRenov;
                  const isFamilyView = renovPerspective === 'family';
                  return (
                  <div key={card.id} className={`bg-zinc-900 border ${isFamilyView ? 'border-pink-500/30' : 'border-zinc-700'} rounded-xl p-3 relative hover:border-pink-500/50 transition-colors`} data-testid={`card-summary-${card.id}`}>
                    <div className="flex items-center gap-1.5 mb-2 min-w-0">
                      <CreditCard className="w-3.5 h-3.5 flex-shrink-0" style={{ color: card.color }} />
                      <span className="text-zinc-200 text-xs font-semibold truncate">{card.name}</span>
                    </div>
                    {editingCard === card.id ? (
                      <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
                        <input type="number" value={cardBalanceInput} onChange={(e) => setCardBalanceInput(e.target.value)}
                          className="w-full px-2 py-1 bg-zinc-800 border border-zinc-600 rounded text-white text-sm min-w-0" autoFocus placeholder="Credit limit" />
                        <button onClick={() => handleUpdateCardBalance(card.id)} className="px-2 py-1 bg-green-600 rounded text-white text-xs flex-shrink-0"><Check className="w-3 h-3" /></button>
                        <button onClick={() => setEditingCard(null)} className="px-2 py-1 bg-zinc-700 rounded text-white text-xs flex-shrink-0"><X className="w-3 h-3" /></button>
                      </div>
                    ) : (
                      <>
                        <div className="cursor-pointer" onClick={() => handleOpenCardBreakdown(card, 'renovations')} data-testid={`card-breakdown-open-${card.id}`}>
                          <p className={`font-bold text-base leading-tight truncate ${isFamilyView ? 'text-pink-300' : 'text-white'}`} data-testid={`card-spent-${card.id}`}>{formatCurrency(renovSpent)}</p>
                          <p className={`text-[10px] mt-1 underline ${isFamilyView ? 'text-pink-400' : 'text-pink-400'}`}>
                            {isFamilyView ? 'Family $ on this card' : 'Renovations only'}
                          </p>
                        </div>
                        <button onClick={(e) => { e.stopPropagation(); setEditingCard(card.id); setCardBalanceInput(card.balance || 0); }}
                          className="absolute top-1.5 right-1.5 text-zinc-500 hover:text-zinc-200 text-[10px]" data-testid={`card-edit-balance-${card.id}`}>
                          edit
                        </button>
                      </>
                    )}
                  </div>
                  );
                })}
              </div>
            )}
            
            {/* Add Expense + Scan Statement */}
            {!showAddExpense ? (
              <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-2">
                <Button onClick={() => setShowAddExpense(true)} className="w-full bg-pink-600 hover:bg-pink-700 py-5" data-testid="add-renovation-expense-btn">
                  <Plus className="w-5 h-5 mr-2" /> Add Renovation Expense
                </Button>
                <Button onClick={() => statementFileInputRef.current?.click()} disabled={scanningStatement} className="bg-purple-600 hover:bg-purple-700 py-5 px-5" data-testid="scan-statement-btn">
                  {scanningStatement ? <><RefreshCw className="w-5 h-5 mr-2 animate-spin" /> Scanning...</> : <><Camera className="w-5 h-5 mr-2" /> Scan Statement</>}
                </Button>
                <input ref={statementFileInputRef} type="file" accept="image/*" multiple className="hidden" onChange={handleStatementUpload} data-testid="statement-file-input" />
              </div>
            ) : (
              <Card className="bg-zinc-900 border-zinc-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white flex items-center justify-between text-base">
                    <span className="flex items-center gap-2"><Plus className="w-4 h-4 text-pink-400" /> Add Renovation Expense</span>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddExpense(false)} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-300 text-sm flex items-center gap-1.5">
                        <Lock className="w-3.5 h-3.5 text-emerald-400" /> My Real Cost <span className="text-zinc-500 text-[10px]">(private — only you)</span> *
                      </Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
                        <Input type="number" placeholder="14500.00" value={newExpense.amount}
                          onChange={(e) => setNewExpense({...newExpense, amount: e.target.value})}
                          className="bg-zinc-800 border-zinc-600 text-white pl-9"
                          data-testid="renov-real-cost-input" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-zinc-300 text-sm flex items-center gap-1.5">
                        <Mail className="w-3.5 h-3.5 text-pink-400" /> Family Charge <span className="text-zinc-500 text-[10px]">(what they see on invoice)</span>
                      </Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-pink-500" />
                        <Input type="number" placeholder={newExpense.amount || "18500.00"} value={newExpense.charge_amount}
                          onChange={(e) => setNewExpense({...newExpense, charge_amount: e.target.value})}
                          className="bg-zinc-800 border-zinc-600 text-white pl-9"
                          data-testid="renov-charge-input" />
                      </div>
                      <p className="text-[10px] text-zinc-500 mt-1 italic">Leave blank if family pays the same as your real cost.</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-zinc-300 text-sm">What was paid for? *</Label>
                      <Input placeholder="Flooring, Cabinets, Plumbing..." value={newExpense.description} onChange={(e) => setNewExpense({...newExpense, description: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-300 text-sm">Date *</Label>
                      <Input type="date" value={newExpense.date} onChange={(e) => setNewExpense({...newExpense, date: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-300 text-sm">Notes</Label>
                      <Input placeholder="Paid to Home Depot, receipt #..." value={newExpense.notes} onChange={(e) => setNewExpense({...newExpense, notes: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                    </div>
                  </div>

                  {/* Credit Card Selector */}
                  {cards.length > 0 && (
                    <div>
                      <Label className="text-zinc-300 text-sm mb-2 block">Paid With</Label>
                      <div className="flex flex-wrap gap-2">
                        {cards.map(card => (
                          <button key={card.id} onClick={() => setNewExpense({...newExpense, card_used: newExpense.card_used === card.id ? '' : card.id})}
                            className={`px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2 transition-all border ${
                              newExpense.card_used === card.id 
                                ? 'border-pink-500 bg-pink-600/20 text-white' 
                                : 'border-zinc-600 bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                            }`}>
                            <div className="w-4 h-3 rounded-sm" style={{ backgroundColor: card.color, border: card.id === 'white' ? '1px solid #666' : 'none' }} />
                            {card.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Paid-in-full toggle removed per user request 2026-02-26 —
                      every entered expense is automatically considered paid. */}

                  {contacts.length > 0 && (
                    <div>
                      <Label className="text-zinc-300 text-sm mb-2 block">Email Family Members</Label>
                      <div className="flex flex-wrap gap-2">
                        {contacts.map(c => (
                          <button key={c.id} onClick={() => toggleContactSelection(c.id)}
                            className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1 transition-all ${
                              selectedContacts.includes(c.id) ? 'bg-pink-600 text-white' : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                            }`}>
                            {selectedContacts.includes(c.id) && <Check className="w-3 h-3" />} {c.name}
                          </button>
                        ))}
                        <button onClick={() => setSelectedContacts(contacts.map(c => c.id))} className="px-3 py-1.5 rounded-full text-xs font-medium bg-zinc-700 text-zinc-300 hover:bg-zinc-600">Select All</button>
                      </div>
                    </div>
                  )}
                  
                  <div className="flex gap-3 pt-1">
                    <Button onClick={handleAddExpense} disabled={savingExpense} className="flex-1 bg-pink-600 hover:bg-pink-700">
                      {savingExpense ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> Add Expense</>}
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddExpense(false)} className="border-zinc-600 text-zinc-300">Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* View Toggle + Duplicate Banner */}
            {!loadingExpenses && expenses.length > 0 && (
              <div className="space-y-2">
                {/* Duplicate Warning Banner */}
                {duplicateGroups.length > 0 && (
                  <Card className="bg-amber-950/40 border-amber-500/40">
                    <CardContent className="p-3 flex items-center justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2 text-amber-200 text-sm">
                        <AlertTriangle className="w-4 h-4 flex-shrink-0" />
                        <span>
                          <strong>{duplicateGroups.length} possible duplicate{duplicateGroups.length === 1 ? '' : 's'}</strong>
                          {' '}detected — same description &amp; amount appears more than once.
                        </span>
                      </div>
                      <Button size="sm" variant="outline" onClick={() => setShowDuplicates(s => !s)}
                        className="border-amber-500/50 text-amber-200 hover:bg-amber-900/40 text-xs h-7"
                        data-testid="review-duplicates-btn">
                        {showDuplicates ? 'Hide' : 'Review duplicates'}
                      </Button>
                    </CardContent>
                  </Card>
                )}
                {showDuplicates && duplicateGroups.length > 0 && (
                  <Card className="bg-zinc-950 border-amber-500/30">
                    <CardContent className="p-3 space-y-3">
                      {duplicateGroups.map((g, gi) => (
                        <div key={gi} className="bg-zinc-900/60 border border-amber-500/20 rounded-lg p-2.5">
                          <p className="text-amber-200 text-xs font-semibold mb-1.5">
                            "{g[0].description}" — {formatCurrency(g[0].amount)} · {g.length} entries
                          </p>
                          <div className="space-y-1">
                            {g.map(e => (
                              <div key={e.id} className="flex items-center justify-between bg-zinc-800/60 rounded px-2 py-1 text-xs">
                                <span className="text-zinc-300 flex items-center gap-2">
                                  <Calendar className="w-3 h-3 text-zinc-500" /> {formatDate(e.date)}
                                  <span className="text-zinc-500">· paid {formatCurrency(getPaidAmount(e))}</span>
                                </span>
                                <button onClick={() => handleDeleteExpense(e.id)} className="text-red-400 hover:text-red-300 text-[11px]" data-testid={`dup-delete-${e.id}`}>
                                  <Trash2 className="w-3 h-3 inline mr-1" />Delete
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}

                {/* View-mode toggle + bulk-select controls */}
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-3 flex-wrap">
                    <p className="text-xs text-zinc-500">
                      {expenses.length} expense{expenses.length !== 1 ? 's' : ''} · {formatCurrency(totalRenovations)} paid
                    </p>
                    {!bulkMode ? (
                      <Button size="sm" variant="outline" onClick={() => setBulkMode(true)}
                        className="border-zinc-700 text-zinc-300 hover:bg-zinc-800 h-7 px-2.5 text-[11px]" data-testid="enter-bulk-mode-btn">
                        <Trash2 className="w-3 h-3 mr-1" /> Select multiple
                      </Button>
                    ) : (
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-pink-300 text-[11px] font-semibold">{bulkSelected.size} selected</span>
                        <Button size="sm" onClick={selectAllVisible}
                          className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 h-7 px-2 text-[11px]" data-testid="bulk-select-all-btn">All</Button>
                        <Button size="sm" onClick={clearBulkSelection}
                          className="bg-zinc-800 hover:bg-zinc-700 text-zinc-200 h-7 px-2 text-[11px]" data-testid="bulk-clear-btn">Clear</Button>
                        <Button size="sm" onClick={handleBulkDelete} disabled={bulkSelected.size === 0}
                          className="bg-red-600 hover:bg-red-700 text-white h-7 px-2.5 text-[11px] disabled:opacity-40" data-testid="bulk-delete-btn">
                          <Trash2 className="w-3 h-3 mr-1" /> Delete {bulkSelected.size > 0 ? `(${bulkSelected.size})` : ''}
                        </Button>
                        <Button size="sm" variant="outline" onClick={exitBulkMode}
                          className="border-zinc-700 text-zinc-400 hover:bg-zinc-800 h-7 px-2 text-[11px]" data-testid="exit-bulk-mode-btn">
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <div className="inline-flex bg-zinc-900 border border-zinc-700 rounded-lg p-0.5" role="tablist">
                    <button
                      onClick={() => setRenovView('cards')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${renovView === 'cards' ? 'bg-pink-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                      data-testid="view-cards-btn">
                      <LayoutGrid className="w-3.5 h-3.5" /> Cards
                    </button>
                    <button
                      onClick={() => setRenovView('compact')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${renovView === 'compact' ? 'bg-pink-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                      data-testid="view-compact-btn">
                      <List className="w-3.5 h-3.5" /> Compact
                    </button>
                    <button
                      onClick={() => setRenovView('table')}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium flex items-center gap-1.5 transition-colors ${renovView === 'table' ? 'bg-pink-600 text-white' : 'text-zinc-400 hover:text-white'}`}
                      data-testid="view-table-btn">
                      <Table2 className="w-3.5 h-3.5" /> Table
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Expenses List */}
            {loadingExpenses ? (
              <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-pink-400 mb-2" /><p className="text-zinc-400">Loading...</p></div>
            ) : expenses.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-700"><CardContent className="py-12 text-center"><Home className="w-12 h-12 text-zinc-600 mx-auto mb-3" /><p className="text-zinc-400">No expenses yet</p></CardContent></Card>
            ) : (
              <>
                {/* ─── Search bar ────────────────────────────────── */}
                <div className="relative mb-3" data-testid="expense-search-wrap">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 pointer-events-none" />
                  <Input
                    value={expenseSearch}
                    onChange={(e) => setExpenseSearch(e.target.value)}
                    placeholder="Search — try 'garage', 'Home Depot', 'Red Card', 'Wells Fargo'…"
                    className="bg-zinc-900 border-zinc-700 text-white pl-9 pr-24 h-10 focus:border-pink-500 focus:ring-pink-500"
                    data-testid="expense-search-input"
                  />
                  {expenseSearch && (
                    <button
                      onClick={() => setExpenseSearch('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-zinc-400 hover:text-white font-medium inline-flex items-center gap-1"
                      data-testid="expense-search-clear"
                      title="Clear search"
                    >
                      <X className="w-3.5 h-3.5" /> Clear
                    </button>
                  )}
                </div>
                {expenseSearch && (
                  <p className="text-[11px] text-pink-300 mb-2" data-testid="expense-search-count">
                    {filteredExpenses.length === 0
                      ? `No matches for "${expenseSearch}"`
                      : `${filteredExpenses.length} of ${expenses.length} matched "${expenseSearch}"`}
                  </p>
                )}
                {renovView === 'cards' ? (
              <div className="space-y-3">
                {filteredExpenses.map(expense => {
                  const paid = getPaidAmount(expense);
                  const remaining = (expense.amount || 0) - paid;
                  const pct = expense.amount > 0 ? Math.min((paid / expense.amount) * 100, 100) : 0;
                  const isEditing = editingRenovation === expense.id;
                  const inputValue = paidInputs[expense.id] !== undefined ? paidInputs[expense.id] : paid.toFixed(2);
                  const expCard = cards.find(c => c.id === expense.card_used);
                  // Feb 2026 — flag any renovation whose real cost is
                  // exactly $1 (a placeholder value the admin drops in
                  // when they haven't logged the real receipt yet).
                  // These rows get a blinking-green outline + a
                  // "PLACEHOLDER — UPDATE COST" ribbon so nothing
                  // slips through to the family invoice with a $1
                  // real cost.  The glow turns off the moment the
                  // value is edited above $1.
                  const isPlaceholderCost = parseFloat(expense.amount || 0) === 1;
                  return (
                  <Card
                    key={expense.id}
                    className={`bg-zinc-900 border-zinc-700 hover:border-zinc-600 transition-colors ${
                      isPlaceholderCost && !isEditing
                        ? 'border-emerald-400/70 shadow-[0_0_16px_rgba(52,211,153,0.55)] animate-pulse ring-2 ring-emerald-400/40'
                        : ''
                    }`}
                    data-testid={`renovation-row-${expense.id}`}
                  >
                    <CardContent className="p-4">
                      {isPlaceholderCost && !isEditing && (
                        <div
                          className="mb-3 flex items-center gap-2 rounded-lg border border-emerald-400/50 bg-emerald-500/15 px-3 py-1.5 text-emerald-300 text-[11px] font-black uppercase tracking-wider"
                          data-testid={`renovation-placeholder-flag-${expense.id}`}
                        >
                          <span className="inline-block w-2 h-2 rounded-full bg-emerald-400 animate-ping" />
                          Placeholder $1 · Update real cost when receipt is logged
                        </div>
                      )}
                      {isEditing ? (
                        /* ── EDIT MODE ── */
                        <div className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-zinc-400 text-xs">Description</Label>
                              <Input value={renovationEditForm.description} onChange={(e) => setRenovationEditForm({...renovationEditForm, description: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1 h-9 text-sm" data-testid={`edit-renovation-desc-${expense.id}`} />
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Date</Label>
                              <Input type="date" value={renovationEditForm.date} onChange={(e) => setRenovationEditForm({...renovationEditForm, date: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1 h-9 text-sm" data-testid={`edit-renovation-date-${expense.id}`} />
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div>
                              <Label className="text-zinc-400 text-xs flex items-center gap-1">
                                <Lock className="w-3 h-3 text-emerald-400" /> My Real Cost
                              </Label>
                              <div className="relative mt-1">
                                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-emerald-500" />
                                <Input type="number" value={renovationEditForm.amount} onChange={(e) => setRenovationEditForm({...renovationEditForm, amount: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white pl-7 h-9 text-sm" data-testid={`edit-renovation-amount-${expense.id}`} />
                              </div>
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs flex items-center gap-1">
                                <Mail className="w-3 h-3 text-pink-400" /> Family Charge
                              </Label>
                              <div className="relative mt-1">
                                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-pink-500" />
                                <Input type="number" value={renovationEditForm.charge_amount}
                                  placeholder={renovationEditForm.amount || '0.00'}
                                  onChange={(e) => setRenovationEditForm({...renovationEditForm, charge_amount: e.target.value})}
                                  className="bg-zinc-800 border-zinc-600 text-white pl-7 h-9 text-sm"
                                  data-testid={`edit-renovation-charge-${expense.id}`} />
                              </div>
                              <p className="text-[10px] text-zinc-500 mt-1">Blank = same as real cost</p>
                            </div>
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Notes</Label>
                            <Input value={renovationEditForm.notes} onChange={(e) => setRenovationEditForm({...renovationEditForm, notes: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1 h-9 text-sm" placeholder="Add notes..." data-testid={`edit-renovation-notes-${expense.id}`} />
                          </div>
                          {cards.length > 0 && (
                            <div>
                              <Label className="text-zinc-400 text-xs mb-1.5 block">Card Used</Label>
                              <div className="flex flex-wrap gap-1.5">
                                {cards.map(card => (
                                  <button key={card.id} onClick={() => setRenovationEditForm({...renovationEditForm, card_used: renovationEditForm.card_used === card.id ? '' : card.id})}
                                    data-testid={`edit-renovation-card-${expense.id}-${card.id}`}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border transition-all ${
                                      renovationEditForm.card_used === card.id ? 'border-pink-500 bg-pink-600/20 text-white' : 'border-zinc-600 bg-zinc-800 text-zinc-400'
                                    }`}>
                                    <span className="w-3 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: card.color, border: card.id === 'white' ? '1px solid #666' : 'none' }} />
                                    {card.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="flex flex-wrap gap-2 pt-1">
                            <Button size="sm" onClick={() => handleSaveRenovationEdit(expense.id, { paidInFull: true })} disabled={savingRenovation} className="bg-green-600 hover:bg-green-700 text-white px-4" data-testid={`save-renovation-${expense.id}`}>
                              {savingRenovation ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Save
                            </Button>
                            {/* "Paid in Full" button removed 2026-02-26 — Save itself now marks paid in full */}
                            <Button size="sm" variant="ghost" onClick={() => setEditingRenovation(null)} className="text-zinc-400">Cancel</Button>
                            <div className="flex-1" />
                            <Button size="sm" variant="ghost" onClick={() => handleDeleteExpense(expense.id)} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* ── VIEW MODE ── */
                        <div className="space-y-3">
                          <div className="flex items-start justify-between gap-2">
                            {bulkMode && (
                              <input type="checkbox" checked={bulkSelected.has(expense.id)} onChange={() => toggleBulkSelect(expense.id)}
                                className="mt-1.5 w-4 h-4 accent-pink-500 cursor-pointer flex-shrink-0"
                                data-testid={`bulk-select-${expense.id}`} />
                            )}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="font-semibold text-white">{expense.description}</h3>
                                <span className={`text-lg font-bold ${renovPerspective === 'family' ? 'text-pink-400' : 'text-emerald-400'}`}>{formatCurrency(displayAmount(expense))}</span>
                                {renovPerspective === 'mine' && expense.charge_amount !== undefined && expense.charge_amount !== null && expense.charge_amount !== '' && parseFloat(expense.charge_amount) !== parseFloat(expense.amount) && (
                                  <span className="text-[10px] px-1.5 py-px bg-pink-500/15 text-pink-300 border border-pink-500/30 rounded" title={`Family is charged ${formatCurrency(expense.charge_amount)}`}>
                                    Family: {formatCurrency(expense.charge_amount)}
                                  </span>
                                )}
                              </div>
                              <div className="flex items-center gap-3 mt-1 text-sm text-zinc-500 flex-wrap">
                                <span className="flex items-center gap-1"><Calendar className="w-3 h-3 flex-shrink-0" /> {formatDate(expense.date)}</span>
                                {expCard && (
                                  <span className="flex items-center gap-1 text-xs">
                                    <span className="w-3 h-2 rounded-sm inline-block flex-shrink-0" style={{ backgroundColor: expCard.color, border: expCard.id === 'white' ? '1px solid #666' : 'none' }} />
                                    {expCard.name}
                                  </span>
                                )}
                                {expense.notes && <span className="text-zinc-400">- {expense.notes}</span>}
                              </div>
                            </div>
                            {!bulkMode && (
                              <Button size="sm" variant="outline" onClick={() => handleStartEditRenovation(expense)}
                                className="border-pink-600/50 text-pink-300 hover:bg-pink-800/30 text-xs h-8 px-3 flex-shrink-0" data-testid={`edit-renovation-btn-${expense.id}`}>
                                Edit
                              </Button>
                            )}
                          </div>

                          {/* Paid So Far block — hidden in Family Invoice view (internal accounting only) */}
                          {renovPerspective === 'mine' && (
                          <div className="bg-zinc-800/50 rounded-lg p-3 border border-zinc-700">
                            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
                              <Label className="text-zinc-300 text-xs font-medium">Paid So Far</Label>
                              <span className="text-xs text-zinc-400">
                                <span className="text-green-400 font-bold">{formatCurrency(paid)}</span> of {formatCurrency(expense.amount)} 
                                <span className="text-zinc-500 ml-1.5">({pct.toFixed(0)}%)</span>
                              </span>
                            </div>
                            {/* Progress bar */}
                            <div className="w-full bg-zinc-900 rounded-full h-2 mb-2 overflow-hidden">
                              <div className={`h-2 rounded-full transition-all ${pct >= 100 ? 'bg-emerald-500' : 'bg-green-500'}`} style={{ width: `${pct}%` }} />
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="relative flex-1 min-w-[140px]">
                                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                                <Input
                                  type="number"
                                  step="0.01"
                                  value={inputValue}
                                  onChange={(e) => setPaidInputs({ ...paidInputs, [expense.id]: e.target.value })}
                                  className="bg-zinc-900 border-zinc-600 text-white pl-7 h-9 text-sm"
                                  placeholder="0.00"
                                  data-testid={`paid-input-${expense.id}`}
                                />
                              </div>
                              <Button size="sm" onClick={() => handleSetPaidAmount(expense.id, parseFloat(paidInputs[expense.id] ?? paid) || 0)}
                                disabled={savingPaidId === expense.id}
                                className="bg-green-600 hover:bg-green-700 text-white h-9 px-3" data-testid={`save-paid-${expense.id}`}>
                                {savingPaidId === expense.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <><Check className="w-3.5 h-3.5 mr-1" /> Save</>}
                              </Button>
                              {/* "Paid in Full" button removed 2026-02-26 — every entered expense is auto-paid */}
                            </div>
                            
                            {/* Add Payment quick action — only when not paid in full */}
                            {(expense.payments || []).length > 0 && (
                              <div className="mt-3 space-y-1.5" data-testid={`renov-payments-list-${expense.id}`}>
                                <p className="text-zinc-400 text-[11px] uppercase tracking-wide">Payments Made</p>
                                {(expense.payments || []).map((p) => {
                                  const isEditing = editingRenovPayment && editingRenovPayment.paymentId === p.id;
                                  const payCard = cards.find(c => c.id === p.card_used);
                                  return isEditing ? (
                                    <div key={p.id} className="bg-zinc-800/60 border border-pink-500/40 rounded-lg p-2.5 space-y-2" data-testid={`renov-payment-edit-${p.id}`}>
                                      <div className="grid grid-cols-2 gap-2">
                                        <div>
                                          <Label className="text-zinc-400 text-[10px]">Amount</Label>
                                          <div className="relative mt-0.5">
                                            <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
                                            <Input type="number" step="0.01" value={renovPayForm.amount}
                                              onChange={(e) => setRenovPayForm({ ...renovPayForm, amount: e.target.value })}
                                              className="bg-zinc-900 border-zinc-600 text-white pl-6 h-8 text-xs" />
                                          </div>
                                        </div>
                                        <div>
                                          <Label className="text-zinc-400 text-[10px]">Date</Label>
                                          <Input type="date" value={renovPayForm.date}
                                            onChange={(e) => setRenovPayForm({ ...renovPayForm, date: e.target.value })}
                                            className="bg-zinc-900 border-zinc-600 text-white h-8 text-xs mt-0.5" />
                                        </div>
                                      </div>
                                      {cards.length > 0 && (
                                        <div>
                                          <Label className="text-zinc-400 text-[10px] mb-1 block">Card</Label>
                                          <div className="flex flex-wrap gap-1">
                                            {cards.map(card => (
                                              <button key={card.id} onClick={() => setRenovPayForm({ ...renovPayForm, card_used: renovPayForm.card_used === card.id ? '' : card.id })}
                                                className={`px-2 py-1 rounded text-[10px] font-medium border flex items-center gap-1 ${
                                                  renovPayForm.card_used === card.id ? 'border-green-500 bg-green-600/20 text-white' : 'border-zinc-600 bg-zinc-800 text-zinc-400'
                                                }`}>
                                                <span className="w-2.5 h-1.5 rounded-sm" style={{ backgroundColor: card.color, border: card.id === 'white' ? '1px solid #666' : 'none' }} />
                                                {card.name}
                                              </button>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      <div className="flex gap-1.5">
                                        <Button size="sm" onClick={handleSaveRenovPayment} className="bg-green-600 hover:bg-green-700 h-7 text-xs px-3" data-testid={`save-renov-payment-${p.id}`}>
                                          <Check className="w-3 h-3 mr-1" /> Save
                                        </Button>
                                        <Button size="sm" variant="ghost" onClick={() => setEditingRenovPayment(null)} className="text-zinc-400 h-7 text-xs">Cancel</Button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div key={p.id} className="flex items-center justify-between bg-zinc-800/40 border border-zinc-700 rounded-lg px-2.5 py-1.5 gap-2" data-testid={`renov-payment-row-${p.id}`}>
                                      <div className="flex items-center gap-2 text-xs flex-wrap min-w-0">
                                        <span className="text-green-400 font-bold">{formatCurrency(p.amount)}</span>
                                        <span className="text-zinc-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(p.date)}</span>
                                        {payCard && (
                                          <span className="px-1.5 py-0.5 rounded text-[9px] font-medium" style={{ backgroundColor: `${payCard.color}30`, color: payCard.color, border: `1px solid ${payCard.color}60` }}>
                                            {payCard.name}
                                          </span>
                                        )}
                                        {p.notes && <span className="text-zinc-500 truncate">- {p.notes}</span>}
                                      </div>
                                      <div className="flex gap-1 flex-shrink-0">
                                        <button onClick={() => startEditRenovPayment(expense.id, p)} className="text-zinc-500 hover:text-pink-400" data-testid={`edit-renov-payment-${p.id}`}>
                                          <span className="text-[10px] underline">edit</span>
                                        </button>
                                        <button onClick={() => handleDeleteRenovPayment(expense.id, p.id)} className="text-zinc-500 hover:text-red-400" data-testid={`delete-renov-payment-${p.id}`}>
                                          <Trash2 className="w-3 h-3" />
                                        </button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}

                            {/* Add Payment quick action — only when not paid in full */}
                            {remaining > 0 && (
                              addingPaymentId === expense.id ? (
                                <div className="mt-3 bg-zinc-900 border border-zinc-700 rounded-lg p-3 space-y-2">
                                  <div className="flex items-center gap-2 flex-wrap">
                                    <span className="text-zinc-300 text-xs font-medium">Payment Amount:</span>
                                    <div className="relative flex-1 min-w-[140px]">
                                      <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                                      <Input
                                        type="number"
                                        step="0.01"
                                        autoFocus
                                        value={addPaymentAmount}
                                        onChange={(e) => setAddPaymentAmount(e.target.value)}
                                        className="bg-zinc-800 border-zinc-600 text-white pl-7 h-9 text-sm"
                                        placeholder="0.00"
                                        data-testid={`add-payment-input-${expense.id}`}
                                      />
                                    </div>
                                  </div>
                                  {cards.length > 0 && (
                                    <div>
                                      <Label className="text-zinc-400 text-xs mb-1.5 block">Which card was used? *</Label>
                                      <div className="flex flex-wrap gap-1.5">
                                        {cards.map(card => (
                                          <button key={card.id}
                                            onClick={() => setAddPaymentCard(card.id === addPaymentCard ? '' : card.id)}
                                            data-testid={`add-payment-card-${expense.id}-${card.id}`}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border flex items-center gap-1.5 transition-all ${
                                              addPaymentCard === card.id ? 'border-green-500 bg-green-600/20 text-white' : 'border-zinc-600 bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                                            }`}>
                                            <span className="w-3 h-2 rounded-sm" style={{ backgroundColor: card.color, border: card.id === 'white' ? '1px solid #666' : 'none' }} />
                                            {card.name}
                                          </button>
                                        ))}
                                      </div>
                                    </div>
                                  )}
                                  <div className="flex items-center gap-2 pt-1">
                                    <Button size="sm" onClick={() => handleQuickAddPayment(expense)}
                                      disabled={savingPaidId === expense.id || !addPaymentAmount || !addPaymentCard}
                                      className="bg-green-600 hover:bg-green-700 disabled:opacity-40 text-white h-9 px-4" data-testid={`add-payment-confirm-${expense.id}`}>
                                      {savingPaidId === expense.id ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <><Plus className="w-3.5 h-3.5 mr-1" /> Add Payment</>}
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => { setAddingPaymentId(null); setAddPaymentAmount(''); setAddPaymentCard(''); }} className="text-zinc-400 h-9">
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <Button size="sm" onClick={() => { setAddingPaymentId(expense.id); setAddPaymentAmount(''); setAddPaymentCard(expense.card_used || ''); }}
                                  className="mt-3 w-full bg-pink-600/20 hover:bg-pink-600/30 text-pink-300 border border-pink-600/30 h-9" data-testid={`add-payment-btn-${expense.id}`}>
                                  <Plus className="w-3.5 h-3.5 mr-1" /> Add Payment Made
                                </Button>
                              )
                            )}
                            {!expense.card_used && (
                              <p className="text-[11px] text-amber-400/80 mt-2">Tip: tap Edit and pick a card so payments count toward the right card balance.</p>
                            )}
                            {paid > 0 && remaining > 0 && (
                              <p className="text-[11px] text-amber-300/80 mt-1.5">Remaining: {formatCurrency(remaining)}</p>
                            )}
                            {paid > 0 && remaining <= 0 && (
                              <p className="text-[11px] text-emerald-400 mt-1.5 font-medium">Fully paid</p>
                            )}
                          </div>
                          )}
                          {/* Family-view footer — show the family-charge summary clean */}
                          {renovPerspective === 'family' && (
                            <div className="bg-pink-950/30 border border-pink-500/20 rounded-lg p-3">
                              <div className="flex items-center justify-between">
                                <Label className="text-pink-300 text-xs font-medium">Family Charge for this Item</Label>
                                <span className="text-pink-300 font-bold text-base">{formatCurrency(displayAmount(expense))}</span>
                              </div>
                              <p className="text-zinc-500 text-[10px] mt-1.5 italic">This is what your family sees on their invoice. Internal payments &amp; real cost are hidden.</p>
                            </div>
                          )}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            ) : renovView === 'compact' ? (
              /* ── COMPACT LIST VIEW ── */
              <Card className="bg-zinc-900 border-zinc-700">
                <CardContent className="p-0 divide-y divide-zinc-800">
                  {filteredExpenses.map(expense => {
                    const paid = getPaidAmount(expense);
                    const shownAmount = displayAmount(expense);
                    const remaining = (expense.amount || 0) - paid;
                    const pct = expense.amount > 0 ? Math.min((paid / expense.amount) * 100, 100) : 0;
                    const fullyPaid = remaining <= 0.001 && expense.amount > 0;
                    const isDup = duplicateExpenseIds.has(expense.id);
                    const expCard = cards.find(c => c.id === expense.card_used);
                    const charge = (expense.charge_amount === null || expense.charge_amount === undefined || expense.charge_amount === '') ? expense.amount : parseFloat(expense.charge_amount);
                    const hasMarkup = charge !== expense.amount;
                    return (
                      <div key={expense.id}
                        className={`px-3 py-2.5 flex items-center gap-3 hover:bg-zinc-800/40 transition-colors ${isDup ? 'border-l-2 border-l-amber-500' : ''} ${bulkMode && bulkSelected.has(expense.id) ? 'bg-pink-900/20' : ''}`}
                        data-testid={`renov-compact-row-${expense.id}`}>
                        {bulkMode && (
                          <input type="checkbox" checked={bulkSelected.has(expense.id)} onChange={() => toggleBulkSelect(expense.id)}
                            className="w-4 h-4 accent-pink-500 cursor-pointer flex-shrink-0"
                            data-testid={`bulk-select-compact-${expense.id}`} />
                        )}
                        {/* Status dot */}
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${fullyPaid ? 'bg-emerald-500' : pct > 0 ? 'bg-amber-400' : 'bg-zinc-600'}`}
                          title={fullyPaid ? 'Paid in full' : pct > 0 ? 'Partial' : 'Unpaid'} />
                        {/* Description + meta */}
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-white font-medium text-sm truncate">{expense.description}</span>
                            {isDup && <AlertTriangle className="w-3 h-3 text-amber-400 flex-shrink-0" />}
                            {hasMarkup && renovPerspective === 'mine' && (
                              <span className="text-[9px] px-1.5 py-px bg-pink-500/15 text-pink-300 border border-pink-500/30 rounded flex-shrink-0" title={`Charged to family: ${formatCurrency(charge)}`}>
                                +{formatCurrency(charge - expense.amount)}
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2 text-[11px] text-zinc-500 mt-0.5 flex-wrap">
                            <Calendar className="w-3 h-3" /> {formatDate(expense.date)}
                            {expCard && (
                              <span className="px-1.5 py-px rounded text-[9px] font-medium" style={{ backgroundColor: `${expCard.color}30`, color: expCard.color, border: `1px solid ${expCard.color}60` }}>
                                {expCard.name}
                              </span>
                            )}
                          </div>
                        </div>
                        {/* Amounts */}
                        <div className="text-right flex-shrink-0">
                          <div className={`font-bold text-sm ${renovPerspective === 'family' ? 'text-pink-300' : 'text-white'}`}>{formatCurrency(shownAmount)}</div>
                          <div className="text-[11px]">
                            {renovPerspective === 'family' ? (
                              <span className="text-zinc-500">family invoice</span>
                            ) : fullyPaid ? (
                              <span className="text-emerald-400">Paid in full</span>
                            ) : (
                              <><span className="text-green-400">{formatCurrency(paid)}</span><span className="text-zinc-500"> / </span><span className="text-amber-300">{formatCurrency(remaining)} left</span></>
                            )}
                          </div>
                        </div>
                        {/* "Paid" button removed 2026-02-26 — auto-paid on entry */}
                        {fullyPaid && (
                          <span className="text-emerald-400 flex-shrink-0" title="Paid in full"><CheckCircle2 className="w-4 h-4" /></span>
                        )}
                        <button onClick={() => handleStartEditRenovation(expense)} className="text-zinc-500 hover:text-pink-400 text-[11px] underline flex-shrink-0" data-testid={`compact-edit-${expense.id}`}>edit</button>
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            ) : (
              /* ── TABLE VIEW ── */
              <Card className="bg-zinc-900 border-zinc-700">
                <CardContent className="p-0 overflow-x-auto">
                  <table className="w-full text-sm" data-testid="renov-table">
                    <thead className="bg-zinc-950/80 border-b border-zinc-700">
                      <tr className="text-left text-[11px] uppercase tracking-wide text-zinc-500">
                        {bulkMode && (
                          <th className="px-2.5 py-2 w-8">
                            <input type="checkbox"
                              checked={bulkSelected.size > 0 && bulkSelected.size === expenses.length}
                              onChange={() => bulkSelected.size === expenses.length ? clearBulkSelection() : selectAllVisible()}
                              className="w-4 h-4 accent-pink-500 cursor-pointer"
                              data-testid="bulk-select-table-header" />
                          </th>
                        )}
                        {[
                          { key: 'date', label: 'Date' },
                          { key: 'description', label: 'Description' },
                          ...(renovPerspective === 'mine' ? [
                            { key: 'amount', label: 'My Cost', align: 'right' },
                            { key: 'charge', label: 'Family $', align: 'right' },
                            { key: 'paid', label: 'Paid', align: 'right' },
                            { key: 'remaining', label: 'Remaining', align: 'right' },
                          ] : [
                            { key: 'charge', label: 'Family Charge', align: 'right' },
                          ]),
                          { key: 'card', label: 'Card' },
                          { key: 'status', label: 'Status' },
                        ].map(col => (
                          <th key={col.key}
                            onClick={() => setRenovSort(s => ({ key: col.key, dir: s.key === col.key && s.dir === 'asc' ? 'desc' : 'asc' }))}
                            className={`px-2.5 py-2 cursor-pointer hover:text-pink-300 select-none ${col.align === 'right' ? 'text-right' : ''}`}>
                            {col.label}
                            {renovSort.key === col.key && (
                              <span className="ml-1 text-pink-400">{renovSort.dir === 'asc' ? '↑' : '↓'}</span>
                            )}
                          </th>
                        ))}
                        <th className="px-2.5 py-2 text-right">Action</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800">
                      {[...filteredExpenses].sort((a, b) => {
                        const dir = renovSort.dir === 'asc' ? 1 : -1;
                        const ka = renovSort.key;
                        const chargeOf = (e) => {
                          const ca = e.charge_amount;
                          return ca === null || ca === undefined || ca === '' ? (parseFloat(e.amount) || 0) : (parseFloat(ca) || 0);
                        };
                        const va = ka === 'paid' ? getPaidAmount(a)
                          : ka === 'remaining' ? (a.amount || 0) - getPaidAmount(a)
                          : ka === 'charge' ? chargeOf(a)
                          : ka === 'card' ? (cards.find(c => c.id === a.card_used)?.name || '')
                          : ka === 'status' ? ((a.amount || 0) - getPaidAmount(a) <= 0.001 ? 1 : getPaidAmount(a) > 0 ? 0 : -1)
                          : a[ka];
                        const vb = ka === 'paid' ? getPaidAmount(b)
                          : ka === 'remaining' ? (b.amount || 0) - getPaidAmount(b)
                          : ka === 'charge' ? chargeOf(b)
                          : ka === 'card' ? (cards.find(c => c.id === b.card_used)?.name || '')
                          : ka === 'status' ? ((b.amount || 0) - getPaidAmount(b) <= 0.001 ? 1 : getPaidAmount(b) > 0 ? 0 : -1)
                          : b[ka];
                        if (va == null) return 1; if (vb == null) return -1;
                        if (typeof va === 'number') return (va - vb) * dir;
                        return String(va).localeCompare(String(vb)) * dir;
                      }).map(expense => {
                        const paid = getPaidAmount(expense);
                        const remaining = (expense.amount || 0) - paid;
                        const fullyPaid = remaining <= 0.001 && expense.amount > 0;
                        const isDup = duplicateExpenseIds.has(expense.id);
                        const expCard = cards.find(c => c.id === expense.card_used);
                        const charge = (expense.charge_amount === null || expense.charge_amount === undefined || expense.charge_amount === '') ? expense.amount : parseFloat(expense.charge_amount);
                        const hasMarkup = charge !== expense.amount;
                        return (
                          <tr key={expense.id}
                            className={`hover:bg-zinc-800/40 ${isDup ? 'bg-amber-950/20' : ''} ${bulkMode && bulkSelected.has(expense.id) ? 'bg-pink-900/20' : ''}`}
                            data-testid={`renov-table-row-${expense.id}`}>
                            {bulkMode && (
                              <td className="px-2.5 py-2">
                                <input type="checkbox" checked={bulkSelected.has(expense.id)} onChange={() => toggleBulkSelect(expense.id)}
                                  className="w-4 h-4 accent-pink-500 cursor-pointer"
                                  data-testid={`bulk-select-table-${expense.id}`} />
                              </td>
                            )}
                            <td className="px-2.5 py-2 text-zinc-300 whitespace-nowrap">{formatDate(expense.date)}</td>
                            <td className="px-2.5 py-2 text-white max-w-[260px] truncate" title={expense.description}>
                              {isDup && <AlertTriangle className="inline w-3 h-3 text-amber-400 mr-1" />}
                              {expense.description}
                            </td>
                            {renovPerspective === 'mine' ? (
                              <>
                                <td className="px-2.5 py-2 text-right text-emerald-300 font-bold whitespace-nowrap">{formatCurrency(expense.amount)}</td>
                                <td className={`px-2.5 py-2 text-right font-bold whitespace-nowrap ${hasMarkup ? 'text-pink-300' : 'text-zinc-500'}`}>
                                  {formatCurrency(charge)}
                                </td>
                                <td className="px-2.5 py-2 text-right text-green-400 whitespace-nowrap">{formatCurrency(paid)}</td>
                                <td className="px-2.5 py-2 text-right whitespace-nowrap">
                                  {remaining > 0 ? <span className="text-amber-300">{formatCurrency(remaining)}</span> : <span className="text-zinc-600">—</span>}
                                </td>
                              </>
                            ) : (
                              <td className="px-2.5 py-2 text-right text-pink-300 font-bold whitespace-nowrap">{formatCurrency(charge)}</td>
                            )}
                            <td className="px-2.5 py-2">
                              {expCard ? (
                                <span className="px-1.5 py-px rounded text-[10px] font-medium" style={{ backgroundColor: `${expCard.color}30`, color: expCard.color, border: `1px solid ${expCard.color}60` }}>
                                  {expCard.name}
                                </span>
                              ) : <span className="text-zinc-600">—</span>}
                            </td>
                            <td className="px-2.5 py-2">
                              {fullyPaid ? (
                                <span className="text-[11px] inline-flex items-center gap-1 text-emerald-400"><CheckCircle2 className="w-3 h-3" />Paid</span>
                              ) : paid > 0 ? (
                                <span className="text-[11px] text-amber-300">Partial</span>
                              ) : (
                                <span className="text-[11px] text-zinc-500">Unpaid</span>
                              )}
                            </td>
                            <td className="px-2.5 py-2 text-right whitespace-nowrap">
                              {/* "Paid" button removed 2026-02-26 — auto-paid on entry */}
                              <button onClick={() => handleStartEditRenovation(expense)} className="text-zinc-500 hover:text-pink-400 text-[11px] underline" data-testid={`table-edit-${expense.id}`}>edit</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </CardContent>
              </Card>
            )}
              </>
            )}
          </div>
        )}

        {/* ════════════════════════════════════════════ */}
        {/* PERSONAL TAB (Randal / Gregory / George)    */}
        {/* ════════════════════════════════════════════ */}
        {currentPersonalTab && (() => {
          const totalReceived = personalPayments.reduce((s, p) => s + (parseFloat(p.amount) || 0), 0);
          const owedBalance = personalTotal - totalReceived;
          return (
          <div className="space-y-4">
            {/* Personal Tab Header */}
            <Card className="bg-gradient-to-r from-purple-900/50 to-indigo-900/50 border-purple-500/30">
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div>
                    <p className="text-purple-300 text-sm font-medium">{currentPersonalTab.name}'s Personal Tab</p>
                    <p className="text-3xl font-bold text-white mt-1" data-testid="personal-tab-balance">{formatCurrency(owedBalance)}</p>
                    <p className="text-purple-300/70 text-xs mt-1">Owed balance ({personalExpenses.length} item{personalExpenses.length !== 1 ? 's' : ''})</p>
                    <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                      <span className="text-zinc-300">Charged: <span className="text-white font-semibold">{formatCurrency(personalTotal)}</span></span>
                      <span className="text-zinc-300">Paid back: <span className="text-green-400 font-semibold">{formatCurrency(totalReceived)}</span></span>
                    </div>
                    {personalTotal > 0 && (
                      <div className="w-full max-w-xs bg-zinc-900 rounded-full h-2 mt-2 overflow-hidden">
                        <div className="bg-green-500 h-2 rounded-full transition-all" style={{ width: `${Math.min((totalReceived / personalTotal) * 100, 100)}%` }} />
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {currentPersonalTab.email ? (
                      <Button size="sm" onClick={() => handleSendTabEmail(currentPersonalTab.id)} disabled={sendingTabEmail === currentPersonalTab.id}
                        className="bg-purple-600 hover:bg-purple-700 text-white text-xs" data-testid="send-tab-email-btn">
                        {sendingTabEmail === currentPersonalTab.id ? <RefreshCw className="w-3 h-3 mr-1 animate-spin" /> : <Mail className="w-3 h-3 mr-1" />}
                        Email Tab to {currentPersonalTab.name}
                      </Button>
                    ) : (
                      <span className="text-xs text-purple-400">Set email below to send</span>
                    )}
                    <Button size="sm" variant="outline" onClick={() => handleCopyTabLink(currentPersonalTab)}
                      className={`text-xs ${copiedLink === currentPersonalTab.id ? 'bg-green-600 border-green-600 text-white' : 'border-purple-500/50 text-purple-300 hover:bg-purple-800'}`}
                      data-testid="copy-tab-link-btn">
                      {copiedLink === currentPersonalTab.id ? <><Check className="w-3 h-3 mr-1" /> Copied!</> : <><Copy className="w-3 h-3 mr-1" /> Copy Link to Text</>}
                    </Button>
                  </div>
                </div>
                
                {/* Email Setting */}
                <div className="mt-3 pt-3 border-t border-purple-500/20">
                  {editingTabEmail === currentPersonalTab.id ? (
                    <div className="flex gap-2">
                      <Input value={tabEmailInput} onChange={(e) => setTabEmailInput(e.target.value)} placeholder={`${currentPersonalTab.name}'s email...`} className="bg-purple-900/50 border-purple-500/30 text-white text-sm flex-1" />
                      <Button size="sm" onClick={() => handleSaveTabEmail(currentPersonalTab.id)} className="bg-green-600 hover:bg-green-700 text-xs"><Check className="w-3 h-3 mr-1" /> Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingTabEmail(null)} className="text-zinc-400 text-xs"><X className="w-3 h-3" /></Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <p className="text-sm text-purple-300">
                        <Mail className="w-3 h-3 inline mr-1" />
                        {currentPersonalTab.email || <span className="text-purple-500 italic">No email set</span>}
                      </p>
                      <Button size="sm" variant="ghost" onClick={() => { setEditingTabEmail(currentPersonalTab.id); setTabEmailInput(currentPersonalTab.email || ''); }}
                        className="text-purple-400 hover:text-white text-xs">Edit Email</Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Credit Card Balances — scoped to THIS tab only */}
            {cards.length > 0 && (
              <div className="grid grid-cols-3 gap-2" data-testid="personal-card-balances-grid">
                {cards.map(card => {
                  const tabSpent = card?.breakdown?.personal?.[currentPersonalTab.id] ?? 0;
                  return (
                    <button key={card.id} onClick={() => handleOpenCardBreakdown(card, 'personal', currentPersonalTab.id)}
                      className="bg-zinc-900 border border-zinc-700 rounded-xl p-3 hover:border-purple-500/50 transition-colors text-left" data-testid={`personal-card-summary-${card.id}`}>
                      <div className="flex items-center gap-1.5 mb-2 min-w-0">
                        <CreditCard className="w-3.5 h-3.5 flex-shrink-0" style={{ color: card.color }} />
                        <span className="text-zinc-200 text-xs font-semibold truncate">{card.name}</span>
                      </div>
                      <p className="text-white font-bold text-base leading-tight truncate" data-testid={`personal-card-spent-${card.id}`}>{formatCurrency(tabSpent)}</p>
                      <p className="text-purple-400 text-[10px] mt-1 underline">{currentPersonalTab.name}'s charges • view</p>
                    </button>
                  );
                })}
              </div>
            )}
            
            {/* Payments Received Section */}
            <Card className="bg-zinc-900 border-zinc-700">
              <CardHeader className="pb-3">
                <CardTitle className="text-white flex items-center justify-between text-base">
                  <span className="flex items-center gap-2"><DollarSign className="w-4 h-4 text-green-400" /> Payments from {currentPersonalTab.name}</span>
                  {!showAddPersonalPayment && (
                    <Button size="sm" onClick={() => setShowAddPersonalPayment(true)} className="bg-green-600 hover:bg-green-700 text-white text-xs h-8" data-testid="add-personal-payment-btn">
                      <Plus className="w-3.5 h-3.5 mr-1" /> Add Payment
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {showAddPersonalPayment && (
                  <div className="bg-zinc-800/60 border border-zinc-700 rounded-lg p-3 space-y-3 mb-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-zinc-400 text-xs">Amount *</Label>
                        <div className="relative mt-1">
                          <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                          <Input type="number" step="0.01" placeholder="0.00" value={newPersonalPayment.amount} onChange={(e) => setNewPersonalPayment({...newPersonalPayment, amount: e.target.value})} className="bg-zinc-900 border-zinc-600 text-white pl-7 h-9 text-sm" data-testid="payment-amount-input" />
                        </div>
                      </div>
                      <div>
                        <Label className="text-zinc-400 text-xs">Date</Label>
                        <Input type="date" value={newPersonalPayment.date} onChange={(e) => setNewPersonalPayment({...newPersonalPayment, date: e.target.value})} className="bg-zinc-900 border-zinc-600 text-white mt-1 h-9 text-sm" />
                      </div>
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs mb-1.5 block">Method *</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {['Cash', 'Zelle', 'Cash App', 'Apple Pay'].map(m => (
                          <button key={m} onClick={() => setNewPersonalPayment({...newPersonalPayment, method: m})}
                            data-testid={`payment-method-${m.replace(' ', '-').toLowerCase()}`}
                            className={`px-3 py-1.5 rounded-lg text-xs font-medium border transition-all ${
                              newPersonalPayment.method === m ? 'border-green-500 bg-green-600/20 text-white' : 'border-zinc-600 bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
                            }`}>{m}</button>
                        ))}
                      </div>
                    </div>
                    <div>
                      <Label className="text-zinc-400 text-xs">Notes</Label>
                      <Input value={newPersonalPayment.notes} onChange={(e) => setNewPersonalPayment({...newPersonalPayment, notes: e.target.value})} className="bg-zinc-900 border-zinc-600 text-white mt-1 h-9 text-sm" placeholder="Confirmation #, what for..." />
                    </div>
                    <label className="flex items-center gap-2 text-xs text-zinc-300 cursor-pointer">
                      <input type="checkbox" checked={newPersonalPayment.send_receipt} onChange={(e) => setNewPersonalPayment({...newPersonalPayment, send_receipt: e.target.checked})}
                        className="w-4 h-4 rounded border-zinc-600 bg-zinc-800" data-testid="payment-receipt-checkbox" />
                      Email receipt to {currentPersonalTab.email || `${currentPersonalTab.name} (set email above first)`}
                    </label>
                    <div className="flex gap-2 pt-1">
                      <Button size="sm" onClick={handleAddPersonalPayment} disabled={savingPersonalPayment} className="bg-green-600 hover:bg-green-700 text-white px-4 h-9" data-testid="save-personal-payment-btn">
                        {savingPersonalPayment ? <><RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> Saving...</> : <><Check className="w-3.5 h-3.5 mr-1" /> Record Payment</>}
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setShowAddPersonalPayment(false)} className="text-zinc-400 h-9">Cancel</Button>
                    </div>
                  </div>
                )}
                {loadingPersonalPayments ? (
                  <div className="text-center py-3 text-zinc-500 text-xs">Loading...</div>
                ) : personalPayments.length === 0 ? (
                  <div className="text-center py-3 text-zinc-500 text-xs">No payments recorded yet.</div>
                ) : (
                  <div className="space-y-1.5">
                    {personalPayments.length > 0 && (
                      <div className="flex items-center justify-end gap-2 mb-1">
                        <BulkBar scope="personal-payments" allIds={personalPayments.map(p => p.id)}
                          count={personalPayments.length}
                          onDelete={handleBulkDeletePersonalPayments} label="payment" />
                      </div>
                    )}
                    {personalPayments.map(p => {
                      const isEditing = editingPersonalPayment === p.id;
                      const checkedHere = inScope('personal-payments') && bulkScopeIds.has(p.id);
                      return isEditing ? (
                        <div key={p.id} className="bg-zinc-800/60 border border-purple-500/40 rounded-lg p-2.5 space-y-2" data-testid={`personal-payment-edit-${p.id}`}>
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <Label className="text-zinc-400 text-[10px]">Amount</Label>
                              <div className="relative mt-0.5">
                                <DollarSign className="absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 text-zinc-500" />
                                <Input type="number" step="0.01" value={personalPayForm.amount}
                                  onChange={(e) => setPersonalPayForm({ ...personalPayForm, amount: e.target.value })}
                                  className="bg-zinc-900 border-zinc-600 text-white pl-6 h-8 text-xs" />
                              </div>
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-[10px]">Date</Label>
                              <Input type="date" value={personalPayForm.date}
                                onChange={(e) => setPersonalPayForm({ ...personalPayForm, date: e.target.value })}
                                className="bg-zinc-900 border-zinc-600 text-white h-8 text-xs mt-0.5" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-[10px] mb-1 block">Method</Label>
                            <div className="flex flex-wrap gap-1">
                              {['Cash', 'Zelle', 'Cash App', 'Apple Pay'].map(m => (
                                <button key={m} onClick={() => setPersonalPayForm({ ...personalPayForm, method: m })}
                                  className={`px-2 py-1 rounded text-[10px] font-medium border ${
                                    personalPayForm.method === m ? 'border-green-500 bg-green-600/20 text-white' : 'border-zinc-600 bg-zinc-800 text-zinc-400'
                                  }`}>{m}</button>
                              ))}
                            </div>
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-[10px]">Notes</Label>
                            <Input value={personalPayForm.notes}
                              onChange={(e) => setPersonalPayForm({ ...personalPayForm, notes: e.target.value })}
                              className="bg-zinc-900 border-zinc-600 text-white h-8 text-xs mt-0.5" />
                          </div>
                          <div className="flex gap-1.5">
                            <Button size="sm" onClick={handleSavePersonalPayment} className="bg-green-600 hover:bg-green-700 h-7 text-xs px-3" data-testid={`save-personal-payment-edit-${p.id}`}>
                              <Check className="w-3 h-3 mr-1" /> Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingPersonalPayment(null)} className="text-zinc-400 h-7 text-xs">Cancel</Button>
                          </div>
                        </div>
                      ) : (
                        <div key={p.id}
                          onClick={() => { if (inScope('personal-payments')) toggleScopeId(p.id); }}
                          className={`flex items-center justify-between bg-zinc-800/60 border ${checkedHere ? 'border-pink-500 bg-pink-900/10' : 'border-zinc-700'} rounded-lg px-3 py-2 gap-2 ${inScope('personal-payments') ? 'cursor-pointer' : ''}`}
                          data-testid={`payment-row-${p.id}`}>
                          {inScope('personal-payments') && (
                            <input type="checkbox" checked={checkedHere}
                              onChange={() => toggleScopeId(p.id)}
                              onClick={(e) => e.stopPropagation()}
                              className="w-4 h-4 accent-pink-500 cursor-pointer mr-1"
                              data-testid={`bulk-personal-payment-${p.id}`} />
                          )}
                          <div className="flex items-center gap-3 text-xs flex-wrap min-w-0">
                            <span className="text-green-400 font-bold text-sm">{formatCurrency(p.amount)}</span>
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-medium bg-purple-900/50 text-purple-300">{p.method || 'Other'}</span>
                            <span className="text-zinc-400 flex items-center gap-1"><Calendar className="w-3 h-3" /> {formatDate(p.date)}</span>
                            {p.notes && <span className="text-zinc-500 truncate">- {p.notes}</span>}
                          </div>
                          {!inScope('personal-payments') && (
                          <div className="flex gap-2 flex-shrink-0">
                            <button onClick={(e) => { e.stopPropagation(); startEditPersonalPayment(p); }} className="text-zinc-500 hover:text-purple-300 text-[10px] underline" data-testid={`edit-payment-${p.id}`}>edit</button>
                            <button onClick={(e) => { e.stopPropagation(); handleDeletePersonalPayment(p.id); }} className="text-zinc-600 hover:text-red-400 transition-colors" data-testid={`delete-payment-${p.id}`}>
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Add Personal Expense */}
            {!showAddPersonalExpense ? (
              <Button onClick={() => setShowAddPersonalExpense(true)} className="w-full bg-purple-600 hover:bg-purple-700 py-5" data-testid="add-personal-expense-btn">
                <Plus className="w-5 h-5 mr-2" /> Add to Tab
              </Button>
            ) : (
              <Card className="bg-zinc-900 border-zinc-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white flex items-center justify-between text-base">
                    <span className="flex items-center gap-2"><Plus className="w-4 h-4 text-purple-400" /> Add to {currentPersonalTab.name}'s Tab</span>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddPersonalExpense(false)} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-300 text-sm">What was it for? *</Label>
                      <Input placeholder="Groceries, gas, materials..." value={newPersonalExpense.description} onChange={(e) => setNewPersonalExpense({...newPersonalExpense, description: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-300 text-sm">Amount *</Label>
                      <div className="relative mt-1">
                        <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                        <Input type="number" placeholder="0.00" value={newPersonalExpense.amount} onChange={(e) => setNewPersonalExpense({...newPersonalExpense, amount: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white pl-9" />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-zinc-300 text-sm">Date *</Label>
                      <Input type="date" value={newPersonalExpense.date} onChange={(e) => setNewPersonalExpense({...newPersonalExpense, date: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-300 text-sm">Category</Label>
                      <select value={newPersonalExpense.category} onChange={(e) => setNewPersonalExpense({...newPersonalExpense, category: e.target.value})}
                        className="w-full mt-1 px-3 py-2 bg-zinc-800 border border-zinc-600 text-white rounded-md text-sm">
                        <option value="">Select...</option>
                        <option value="Food">Food</option>
                        <option value="Gas">Gas</option>
                        <option value="Materials">Materials</option>
                        <option value="Bills">Bills</option>
                        <option value="Personal">Personal</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div>
                      <Label className="text-zinc-300 text-sm">Paid by</Label>
                      <Input placeholder="Who paid?" value={newPersonalExpense.paid_by} onChange={(e) => setNewPersonalExpense({...newPersonalExpense, paid_by: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                    </div>
                  </div>
                  <div>
                    <Label className="text-zinc-300 text-sm">Notes</Label>
                    <Input placeholder="Any details..." value={newPersonalExpense.notes} onChange={(e) => setNewPersonalExpense({...newPersonalExpense, notes: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                  </div>
                  {/* Card selector */}
                  {cards.length > 0 && (
                    <div>
                      <Label className="text-zinc-300 text-sm mb-1.5 block">Card Used</Label>
                      <div className="flex flex-wrap gap-1.5">
                        {cards.map(card => (
                          <button key={card.id} onClick={() => setNewPersonalExpense({...newPersonalExpense, card_used: newPersonalExpense.card_used === card.id ? '' : card.id})}
                            className={`px-3 py-2 rounded-lg text-sm font-medium flex items-center gap-1.5 border transition-all ${
                              newPersonalExpense.card_used === card.id ? 'border-purple-500 bg-purple-600/20 text-white' : 'border-zinc-600 bg-zinc-800 text-zinc-400'
                            }`}>
                            <span className="w-3.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: card.color, border: card.id === 'white' ? '1px solid #666' : 'none' }} />
                            {card.name}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  <div className="flex gap-3 pt-1">
                    <Button onClick={handleAddPersonalExpense} disabled={savingPersonalExpense} className="flex-1 bg-purple-600 hover:bg-purple-700">
                      {savingPersonalExpense ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> Add</>}
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddPersonalExpense(false)} className="border-zinc-600 text-zinc-300">Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {/* Personal Expenses List */}
            {loadingPersonalExpenses ? (
              <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-purple-400 mb-2" /><p className="text-zinc-400">Loading...</p></div>
            ) : personalExpenses.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-700"><CardContent className="py-12 text-center"><User className="w-12 h-12 text-zinc-600 mx-auto mb-3" /><p className="text-zinc-400">No items on {currentPersonalTab.name}'s tab yet</p></CardContent></Card>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs text-zinc-500">
                    {personalExpenses.length} item{personalExpenses.length !== 1 ? 's' : ''} on {currentPersonalTab.name}'s tab
                  </p>
                  <BulkBar scope="personal-expenses" allIds={personalExpenses.map(e => e.id)}
                    count={personalExpenses.length}
                    onDelete={handleBulkDeletePersonalExpenses} label="expense" />
                </div>
                {personalExpenses.map(exp => {
                  const expCard = cards.find(c => c.id === exp.card_used);
                  const checkedHere = inScope('personal-expenses') && bulkScopeIds.has(exp.id);
                  return (
                  <Card key={exp.id} className={`bg-zinc-900 border ${checkedHere ? 'border-pink-500 bg-pink-900/10' : 'border-zinc-700 hover:border-purple-500/50'} transition-colors ${inScope('personal-expenses') ? '' : 'cursor-pointer'}`}
                    onClick={() => {
                      if (inScope('personal-expenses')) { toggleScopeId(exp.id); return; }
                      if (editingExpense !== exp.id) handleEditExpense(exp);
                    }}>
                    <CardContent className="p-4">
                      {inScope('personal-expenses') && (
                        <div className="flex items-center mb-2">
                          <input type="checkbox" checked={checkedHere}
                            onChange={() => toggleScopeId(exp.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 accent-pink-500 cursor-pointer"
                            data-testid={`bulk-personal-expense-${exp.id}`} />
                          <span className="ml-2 text-pink-300 text-[11px]">Tap row to select</span>
                        </div>
                      )}
                      {editingExpense === exp.id ? (
                        /* ── EDIT MODE ── */
                        <div className="space-y-3" onClick={(e) => e.stopPropagation()}>
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <Label className="text-zinc-400 text-xs">Description</Label>
                              <Input value={editForm.description} onChange={(e) => setEditForm({...editForm, description: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1 h-9 text-sm" />
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Amount</Label>
                              <div className="relative mt-1">
                                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
                                <Input type="number" value={editForm.amount} onChange={(e) => setEditForm({...editForm, amount: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white pl-7 h-9 text-sm" />
                              </div>
                            </div>
                          </div>
                          <div className="grid grid-cols-3 gap-2">
                            <div>
                              <Label className="text-zinc-400 text-xs">Date</Label>
                              <Input type="date" value={editForm.date} onChange={(e) => setEditForm({...editForm, date: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1 h-9 text-sm" />
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Category</Label>
                              <select value={editForm.category} onChange={(e) => setEditForm({...editForm, category: e.target.value})}
                                className="w-full mt-1 px-2 py-2 bg-zinc-800 border border-zinc-600 text-white rounded-md text-xs h-9">
                                <option value="">Select...</option>
                                <option value="Food">Food</option>
                                <option value="Gas">Gas</option>
                                <option value="Materials">Materials</option>
                                <option value="Bills">Bills</option>
                                <option value="Personal">Personal</option>
                                <option value="Other">Other</option>
                              </select>
                            </div>
                            <div>
                              <Label className="text-zinc-400 text-xs">Paid by</Label>
                              <Input value={editForm.paid_by} onChange={(e) => setEditForm({...editForm, paid_by: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1 h-9 text-sm" />
                            </div>
                          </div>
                          <div>
                            <Label className="text-zinc-400 text-xs">Notes</Label>
                            <Input value={editForm.notes} onChange={(e) => setEditForm({...editForm, notes: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1 h-9 text-sm" placeholder="Add notes..." />
                          </div>
                          {/* Card selector */}
                          {cards.length > 0 && (
                            <div>
                              <Label className="text-zinc-400 text-xs mb-1.5 block">Card Used</Label>
                              <div className="flex flex-wrap gap-1.5">
                                {cards.map(card => (
                                  <button key={card.id} onClick={() => setEditForm({...editForm, card_used: editForm.card_used === card.id ? '' : card.id})}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 border transition-all ${
                                      editForm.card_used === card.id ? 'border-purple-500 bg-purple-600/20 text-white' : 'border-zinc-600 bg-zinc-800 text-zinc-400'
                                    }`}>
                                    <span className="w-3 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: card.color, border: card.id === 'white' ? '1px solid #666' : 'none' }} />
                                    {card.name}
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                          <div className="flex gap-2 pt-1">
                            <Button size="sm" onClick={() => handleSaveEdit(exp.id)} disabled={savingEdit} className="bg-green-600 hover:bg-green-700 text-white px-4">
                              {savingEdit ? <RefreshCw className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Check className="w-3.5 h-3.5 mr-1" />} Save
                            </Button>
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); setEditingExpense(null); }} className="text-zinc-400">Cancel</Button>
                            <div className="flex-1" />
                            <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); handleDeletePersonalExpense(exp.id); }} className="text-red-400 hover:text-red-300 hover:bg-red-500/10">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        /* ── VIEW MODE ── */
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h3 className="font-semibold text-white text-sm">{exp.description}</h3>
                              <span className="text-base font-bold text-purple-400">{formatCurrency(exp.amount)}</span>
                            </div>
                            <div className="flex items-center gap-2 mt-1 flex-wrap text-xs text-zinc-500">
                              <span className="flex items-center gap-1"><Calendar className="w-3 h-3 flex-shrink-0" /> {formatDate(exp.date)}</span>
                              {exp.category && <span className="px-1.5 py-0.5 bg-purple-900/50 text-purple-300 rounded-full text-xs">{exp.category}</span>}
                              {expCard && (
                                <span className="flex items-center gap-1">
                                  <span className="w-3 h-2 rounded-sm flex-shrink-0" style={{ backgroundColor: expCard.color, border: expCard.id === 'white' ? '1px solid #666' : 'none' }} />
                                  <span className="text-zinc-400">{expCard.name}</span>
                                </span>
                              )}
                              {exp.paid_by && <span className="text-zinc-400">by: {exp.paid_by}</span>}
                            </div>
                            {exp.notes && <p className="text-xs text-zinc-500 mt-1">{exp.notes}</p>}
                          </div>
                          <span className="text-zinc-600 text-xs ml-2 flex-shrink-0">edit</span>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                  );
                })}
              </div>
            )}
          </div>
          );
        })()}

        {/* ════════════════════════════════════════════ */}
        {/* CREDIT CARD PAYBACK TAB (2026-02-08 user request) */}
        {/* Real cost aggregation across the 4 renovation cards.       */}
        {/* Per-card total = renovation real cost + personal-tab total.*/}
        {/* ════════════════════════════════════════════ */}
        {activeTab === 'cardpayback' && (
          <div className="space-y-4" data-testid="cardpayback-content">
            <div className="p-5 rounded-2xl bg-gradient-to-br from-rose-950/60 via-rose-900/30 to-slate-950 border-2 border-rose-500/40">
              <div className="flex items-center gap-2 mb-2">
                <CreditCard className="w-5 h-5 text-rose-300" />
                <div className="text-xs uppercase tracking-wider font-bold text-rose-300">
                  Total to pay back across all cards
                </div>
              </div>
              <div className="text-4xl sm:text-5xl font-black text-white" data-testid="payback-grand-total">
                ${paybackGrandTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-rose-200/80 mt-1">
                Renovation real cost + personal-tab charges (Greg + Randal) — matches the small tiles on each tab.
              </div>
            </div>

            {PAYBACK_CARD_IDS.map((cid) => {
              const row = paybackTotals[cid] || { card: {}, renovations: 0, personal: 0, total: 0 };
              const card = row.card || {};
              const color = card.color || '#ec4899';
              const expanded = paybackExpanded[cid] === true;
              const items = paybackItems[cid] || [];
              const itemsLoading = paybackItemsLoading[cid];
              return (
                <div
                  key={cid}
                  className="rounded-xl border-2 bg-zinc-900/60 overflow-hidden"
                  style={{ borderColor: color + '55' }}
                  data-testid={`payback-card-${cid}`}
                >
                  <button
                    type="button"
                    className="w-full flex items-center justify-between p-4 hover:bg-white/[0.03] transition"
                    onClick={() => {
                      const willOpen = !expanded;
                      setPaybackExpanded((prev) => ({ ...prev, [cid]: willOpen }));
                      if (willOpen) loadPaybackItems(cid);
                    }}
                    data-testid={`payback-toggle-${cid}`}
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center border"
                        style={{ backgroundColor: color + '22', borderColor: color + '66' }}
                      >
                        <CreditCard className="w-5 h-5" style={{ color }} />
                      </div>
                      <div className="text-left">
                        <div className="font-bold text-white text-base">{card.name || cid}</div>
                        <div className="text-xs text-zinc-400">
                          Renov ${row.renovations.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          {' + '}
                          Personal ${row.personal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">To pay back</div>
                      <div className="text-2xl font-black text-white" data-testid={`payback-total-${cid}`}>
                        ${row.total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    </div>
                  </button>

                  {expanded && (
                    <div className="border-t border-white/5" data-testid={`payback-list-${cid}`}>
                      {itemsLoading ? (
                        <div className="p-4 text-center text-zinc-500 text-sm flex items-center justify-center gap-2">
                          <RefreshCw className="w-4 h-4 animate-spin" /> Loading items…
                        </div>
                      ) : items.length === 0 ? (
                        <div className="p-4 text-center text-zinc-500 text-sm">
                          No renovation or personal charges recorded on this card.
                        </div>
                      ) : (
                        <div className="divide-y divide-white/5">
                          {items.map((it, i) => (
                            <div
                              key={it.id || `${cid}-${i}`}
                              className="px-4 py-2.5 flex items-start justify-between gap-3 hover:bg-white/[0.02]"
                              data-testid={`payback-item-${cid}-${i}`}
                            >
                              <div className="flex-1 min-w-0">
                                <div className="text-sm text-white truncate">
                                  {it.description || '(untitled)'}
                                </div>
                                <div className="text-[10px] text-zinc-500 mt-0.5 flex flex-wrap gap-x-2">
                                  <span className={`uppercase font-bold tracking-wider ${it.type === 'renovation_payment' ? 'text-pink-300' : 'text-violet-300'}`}>
                                    {it.type === 'renovation_payment' ? 'RENOVATION' : (it.tab_name || 'PERSONAL')}
                                  </span>
                                  {it.date && <span>· {it.date}</span>}
                                  {it.method && <span>· {it.method}</span>}
                                  {it.notes && <span className="italic truncate">· {it.notes}</span>}
                                </div>
                              </div>
                              <div className="text-sm font-bold text-white whitespace-nowrap">
                                ${Number(it.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {/* CONTACTS TAB removed 2026-02-08 per user request */}

        {/* ════════════════════════════════════════════ */}
        {/* CONTACTS TAB */}
        {/* ════════════════════════════════════════════ */}

        {/* ════════════════════════════════════════════ */}
        {/* CONTACTS TAB */}
        {/* ════════════════════════════════════════════ */}
        {activeTab === 'contacts' && (
          <div className="space-y-4">
            {!showAddContact ? (
              <Button onClick={() => setShowAddContact(true)} className="w-full bg-pink-600 hover:bg-pink-700 py-5" data-testid="add-contact-btn">
                <Plus className="w-5 h-5 mr-2" /> Add Family Member
              </Button>
            ) : (
              <Card className="bg-zinc-900 border-zinc-700">
                <CardHeader className="pb-3">
                  <CardTitle className="text-white flex items-center justify-between text-base">
                    <span className="flex items-center gap-2"><Users className="w-4 h-4 text-pink-400" /> Add Family Member</span>
                    <Button variant="ghost" size="sm" onClick={() => setShowAddContact(false)} className="text-zinc-400 hover:text-white"><X className="w-4 h-4" /></Button>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-zinc-300 text-sm">Name *</Label>
                      <Input placeholder="Uncle John, Aunt Mary..." value={newContact.name} onChange={(e) => setNewContact({...newContact, name: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                    </div>
                    <div>
                      <Label className="text-zinc-300 text-sm">Email *</Label>
                      <Input type="email" placeholder="their@email.com" value={newContact.email} onChange={(e) => setNewContact({...newContact, email: e.target.value})} className="bg-zinc-800 border-zinc-600 text-white mt-1" />
                    </div>
                  </div>
                  <div className="flex gap-3 pt-1">
                    <Button onClick={handleAddContact} disabled={savingContact} className="flex-1 bg-pink-600 hover:bg-pink-700">
                      {savingContact ? <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Plus className="w-4 h-4 mr-2" /> Add</>}
                    </Button>
                    <Button variant="outline" onClick={() => setShowAddContact(false)} className="border-zinc-600 text-zinc-300">Cancel</Button>
                  </div>
                </CardContent>
              </Card>
            )}
            
            {loadingContacts ? (
              <div className="text-center py-8"><RefreshCw className="w-6 h-6 animate-spin mx-auto text-pink-400 mb-2" /><p className="text-zinc-400">Loading...</p></div>
            ) : contacts.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-700"><CardContent className="py-12 text-center"><Users className="w-12 h-12 text-zinc-600 mx-auto mb-3" /><p className="text-zinc-400">No contacts yet</p></CardContent></Card>
            ) : (
              <div className="grid gap-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <p className="text-xs text-zinc-500">
                    {contacts.length} contact{contacts.length !== 1 ? 's' : ''}
                  </p>
                  <BulkBar scope="contacts" allIds={contacts.map(c => c.id)}
                    count={contacts.length} onDelete={handleBulkDeleteContacts} label="contact" />
                </div>
                {contacts.map(c => {
                  const isEditing = editingContact === c.id;
                  const checkedHere = inScope('contacts') && bulkScopeIds.has(c.id);
                  return (
                    <Card key={c.id} className={`bg-zinc-900 border ${checkedHere ? 'border-pink-500 bg-pink-900/10' : 'border-zinc-700'}`}
                      onClick={() => { if (inScope('contacts') && !isEditing) toggleScopeId(c.id); }}>
                      <CardContent className="p-4 cursor-pointer">
                        {inScope('contacts') && !isEditing && (
                          <input type="checkbox" checked={checkedHere}
                            onChange={() => toggleScopeId(c.id)}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 accent-pink-500 cursor-pointer mb-2"
                            data-testid={`bulk-contact-${c.id}`} />
                        )}
                        {isEditing ? (
                          <div className="space-y-2" data-testid={`edit-contact-form-${c.id}`}>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                              <div>
                                <Label className="text-zinc-400 text-xs">Name</Label>
                                <Input value={contactForm.name} onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                                  className="bg-zinc-800 border-zinc-600 text-white mt-1 h-9 text-sm" data-testid={`edit-contact-name-${c.id}`} />
                              </div>
                              <div>
                                <Label className="text-zinc-400 text-xs">Email</Label>
                                <Input type="email" value={contactForm.email} onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                                  className="bg-zinc-800 border-zinc-600 text-white mt-1 h-9 text-sm" data-testid={`edit-contact-email-${c.id}`} />
                              </div>
                            </div>
                            <div className="flex gap-2 pt-1">
                              <Button size="sm" onClick={handleSaveContact} className="bg-green-600 hover:bg-green-700 h-8 text-xs" data-testid={`save-contact-${c.id}`}>
                                <Check className="w-3.5 h-3.5 mr-1" /> Save
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => setEditingContact(null)} className="text-zinc-400 h-8 text-xs">Cancel</Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-rose-600 rounded-full flex items-center justify-center text-white font-bold">
                                {c.name.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="font-medium text-white">{c.name}</p>
                                <p className="text-sm text-zinc-500">{c.email}</p>
                              </div>
                            </div>
                            <div className="flex gap-1">
                              <Button variant="ghost" size="sm" onClick={() => startEditContact(c)} className="text-zinc-400 hover:text-pink-400 hover:bg-pink-500/10" data-testid={`edit-contact-${c.id}`}>
                                <span className="text-xs">Edit</span>
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDeleteContact(c.id, c.name)} className="text-zinc-500 hover:text-red-400 hover:bg-red-500/10">
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ── Credit card reminder manager modal (Feb 26, 2026) ─────────────
            Per user request: edit the 3 cards used for renovations and their
            monthly due day. Reminders are sent automatically 1 & 2 days
            before via APScheduler. The payment AMOUNT may change so we never
            ask for or store it. */}
        {showReminderCardManager && (
          <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-3" onClick={() => { setShowReminderCardManager(false); setEditingReminderCard(null); }} data-testid="moms-house-reminder-modal">
            <div className="bg-zinc-900 border border-amber-500/40 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-gradient-to-r from-amber-900/80 to-rose-900/80 backdrop-blur px-5 py-4 border-b border-amber-700/40 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-white">Credit Card Payment Reminders</h3>
                  <p className="text-[11px] text-amber-200 mt-0.5">Email me 1 & 2 days before each due date</p>
                </div>
                <Button variant="ghost" onClick={() => { setShowReminderCardManager(false); setEditingReminderCard(null); }} className="text-zinc-300 hover:text-white h-8 w-8 p-0"><X className="w-4 h-4" /></Button>
              </div>
              <div className="p-4 space-y-3">
                {creditReminderCards.length === 0 && editingReminderCard !== 'new' && (
                  <p className="text-zinc-400 text-sm text-center py-4">No reminder cards yet — add one to get heads-up emails before your due date.</p>
                )}
                {creditReminderCards.map(c => (
                  <div key={c.id} className="bg-zinc-800/60 border border-zinc-700 rounded-xl p-3" data-testid={`moms-house-reminder-card-${c.id}`}>
                    {editingReminderCard === c.id ? (
                      <div className="space-y-2">
                        <Input value={reminderCardForm.name} onChange={e => setReminderCardForm({ ...reminderCardForm, name: e.target.value })} placeholder="Card name (e.g. Silver 36K)" className="bg-zinc-900 border-amber-700/40 text-white h-9 text-sm" />
                        <div className="grid grid-cols-2 gap-2">
                          <Input type="number" min={1} max={31} value={reminderCardForm.due_day} onChange={e => setReminderCardForm({ ...reminderCardForm, due_day: e.target.value })} placeholder="Due day (1-31)" className="bg-zinc-900 border-amber-700/40 text-white h-9 text-sm" />
                          <Input value={reminderCardForm.limit_label} onChange={e => setReminderCardForm({ ...reminderCardForm, limit_label: e.target.value })} placeholder="Limit (optional)" className="bg-zinc-900 border-amber-700/40 text-white h-9 text-sm" />
                        </div>
                        <div className="flex gap-2">
                          <Button onClick={saveReminderCard} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs flex-1">Save</Button>
                          <Button onClick={() => setEditingReminderCard(null)} variant="outline" size="sm" className="border-zinc-700 h-8 text-xs">Cancel</Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="text-white font-bold text-sm">{c.name}</p>
                          <p className="text-zinc-400 text-xs">Due day <strong className="text-amber-300">{c.due_day}</strong> of every month{c.limit_label ? ` · ${c.limit_label}` : ''}</p>
                          {nextDueDate(c.due_day) && (
                            <p className="text-zinc-500 text-[10px] mt-0.5">Next: {nextDueDate(c.due_day).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</p>
                          )}
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button onClick={() => sendTestReminder(c.id, c.name)} size="sm" variant="outline" className="border-amber-700/40 text-amber-200 h-7 text-[10px] px-2" data-testid={`moms-house-test-reminder-${c.id}`}>Send test</Button>
                          <div className="flex gap-1">
                            <Button onClick={() => startEditReminderCard(c)} size="sm" variant="outline" className="border-zinc-700 h-7 px-2"><Edit2 className="w-3 h-3" /></Button>
                            <Button onClick={() => deleteReminderCard(c.id, c.name)} size="sm" variant="outline" className="border-red-700/40 text-red-300 h-7 px-2"><Trash2 className="w-3 h-3" /></Button>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {editingReminderCard === 'new' && (
                  <div className="bg-emerald-950/30 border border-emerald-700/40 rounded-xl p-3 space-y-2">
                    <p className="text-emerald-300 text-[11px] font-bold uppercase tracking-wide">Add new card</p>
                    <Input value={reminderCardForm.name} onChange={e => setReminderCardForm({ ...reminderCardForm, name: e.target.value })} placeholder="Card name (e.g. Silver 36K)" className="bg-zinc-900 border-emerald-700/40 text-white h-9 text-sm" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input type="number" min={1} max={31} value={reminderCardForm.due_day} onChange={e => setReminderCardForm({ ...reminderCardForm, due_day: e.target.value })} placeholder="Due day (1-31)" className="bg-zinc-900 border-emerald-700/40 text-white h-9 text-sm" />
                      <Input value={reminderCardForm.limit_label} onChange={e => setReminderCardForm({ ...reminderCardForm, limit_label: e.target.value })} placeholder="Limit (optional)" className="bg-zinc-900 border-emerald-700/40 text-white h-9 text-sm" />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={saveReminderCard} className="bg-emerald-600 hover:bg-emerald-700 h-8 text-xs flex-1">Add card</Button>
                      <Button onClick={() => setEditingReminderCard(null)} variant="outline" size="sm" className="border-zinc-700 h-8 text-xs">Cancel</Button>
                    </div>
                  </div>
                )}
                {editingReminderCard === null && (
                  <Button onClick={startAddReminderCard} variant="outline" className="w-full border-dashed border-emerald-700/40 text-emerald-300 hover:bg-emerald-900/30 h-9 text-xs" data-testid="moms-house-add-reminder-card">
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add another credit card
                  </Button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
